import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Snackbar, Alert } from "@mui/material";
import {
  Inventory2Outlined,
  StorefrontOutlined,
  SearchOutlined,
  AddOutlined as AddIcon,
  DeleteOutlineOutlined as DeleteIcon,
  SaveOutlined as SaveIcon,
  KeyboardOutlined as KeyboardIcon,
  CloseOutlined,
  StraightenOutlined,
  CalculateOutlined,
  PercentOutlined,
  RestartAltOutlined,
  ArrowBackOutlined,
} from "@mui/icons-material";
import { BASE_URL } from "../constants";
import axios from "axios";

// ============================================================
// THEME & DESIGN TOKENS (Matching Products.js)
// ============================================================
const THEME = {
  primary: "#6D28D9",
  primaryDark: "#5B21B6",
  accent: "#8B5CF6",
  light: "#F5F3FF",
  lighter: "#FAF9FF",
  border: "#DDD6FE",
  cardBorder: "#E2E8F0",
  iconBg: "#EDE9FE",
  text: "#1E293B",
  bodyText: "#334155",
  muted: "#64748B",
  lightText: "#94A3B8",
  success: "#059669",
  danger: "#EF4444",
  dangerBg: "#FEF2F2",
  dangerBorder: "#FEE2E2",
};

const cardStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
};

const labelStyle = {
  display: "block",
  marginBottom: "6px",
  fontSize: "12.5px",
  fontWeight: 600,
  color: "rgb(142 142 142)",
};

const formLabelStyle = {
  display: "block",
  marginBottom: "3px",
  fontSize: "11px",
  fontWeight: 700,
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: "0.2px",
};

const inputBaseStyle = {
  height: "38px",
  width: "100%",
  boxSizing: "border-box",
  padding: "0 10px",
  borderRadius: "7px",
  border: "1px solid #E2E8F0",
  background: "#FFFFFF",
  fontSize: "15.5px",
  color: "#1E293B",
  outline: "none",
  transition:
    "border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
};

const tableHeadStyle = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  padding: "9px 10px",
  fontSize: "12.5px",
  fontWeight: 700,
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  border: "1px solid #CBD5E1",
  background: "#F1F5F9",
  textAlign: "left",
  whiteSpace: "nowrap",
  boxSizing: "border-box",
};

const INITIAL_SUPPLIERS = [];

const INITIAL_ACCOUNTS = [
  { id: "ACC001", name: "Cash Account" },
  { id: "ACC002", name: "Bank Account" },
  { id: "ACC003", name: "HDFC Bank A/c" },
  { id: "ACC004", name: "SBI Bank A/c" },
  { id: "ACC005", name: "ICICI Bank A/c" },
  { id: "ACC006", name: "UPI / QR / Online" },
  { id: "ACC007", name: "Petty Cash" },
  { id: "ACC008", name: "Credit / Debit Card" },
];

// ============================================================
// DISC TYPE ROLLER TOGGLE (% / Rs with 3D Drum Peeking)
// ============================================================
const DiscTypeRoller = ({
  id,
  value,
  onChange,
  onKeyDown,
  title,
  height = "32px",
  width = "38px",
  borderless = false,
  className = "",
}) => {
  const isPercent = value === "%";
  const buttonRef = useRef(null);

  useEffect(() => {
    const el = buttonRef.current;
    if (!el) return;

    const onWheelHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.deltaY < 0) {
        onChange("%");
      } else if (e.deltaY > 0) {
        onChange("Rs");
      }
    };

    el.addEventListener("wheel", onWheelHandler, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheelHandler);
    };
  }, [onChange]);

  const handleKeyDownInternal = (e) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      onChange("%");
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      onChange("Rs");
      return;
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <button
      ref={buttonRef}
      id={id}
      type="button"
      tabIndex={0}
      className={`receipt-grid-roller-btn ${className}`.trim()}
      title={
        title ||
        (isPercent
          ? "Selected: % (Click or ↓ for Rs, Scroll to roll)"
          : "Selected: Rs (Click or ↑ for %, Scroll to roll)")
      }
      onClick={() => onChange(isPercent ? "Rs" : "%")}
      onKeyDown={handleKeyDownInternal}
      onFocus={(e) => {
        if (!borderless) {
          e.currentTarget.style.borderColor = THEME.accent;
          e.currentTarget.style.boxShadow = "0 0 0 2px rgba(109,40,217,0.25)";
          e.currentTarget.style.background = "#EDE9FE";
        }
      }}
      onBlur={(e) => {
        if (!borderless) {
          e.currentTarget.style.borderColor = THEME.border;
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.background = THEME.iconBg;
        }
      }}
      style={{
        height,
        width,
        minWidth: width,
        padding: 0,
        position: "relative",
        overflow: "hidden",
        borderRadius: borderless ? 0 : "6px",
        border: borderless ? "none" : `1px solid ${THEME.border}`,
        borderLeft: borderless ? "1px solid #CBD5E1" : undefined,
        background: borderless ? "transparent" : THEME.iconBg,
        cursor: "pointer",
        outline: "none",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        userSelect: "none",
        boxSizing: "border-box",
        transition:
          "border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
      }}
    >
      {/* Vertical sliding drum track */}
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          transform: isPercent ? "translateY(25%)" : "translateY(-25%)",
          transition: "transform 0.22s cubic-bezier(0.16, 1, 0.3, 1)",
          willChange: "transform",
          pointerEvents: "none",
        }}
      >
        {/* % slot (Top half) */}
        <div
          style={{
            height: "50%",
            width: "100%",
            display: "flex",
            alignItems: isPercent ? "center" : "flex-end",
            justifyContent: "center",
            paddingBottom: isPercent ? 0 : "2px",
            fontSize: isPercent ? "14px" : "10px",
            fontWeight: isPercent ? 800 : 600,
            color: isPercent ? THEME.primary : "#94A3B8",
            opacity: isPercent ? 1 : 0.3,
            transition: "all 0.2s ease",
            lineHeight: 1,
            userSelect: "none",
          }}
        >
          %
        </div>

        {/* Rs slot (Bottom half) */}
        <div
          style={{
            height: "50%",
            width: "100%",
            display: "flex",
            alignItems: !isPercent ? "center" : "flex-start",
            justifyContent: "center",
            paddingTop: !isPercent ? 0 : "2px",
            fontSize: !isPercent ? "13px" : "10px",
            fontWeight: !isPercent ? 800 : 600,
            color: !isPercent ? THEME.primary : "#94A3B8",
            opacity: !isPercent ? 1 : 0.3,
            transition: "all 0.2s ease",
            lineHeight: 1,
            userSelect: "none",
          }}
        >
          Rs
        </div>
      </div>
    </button>
  );
};

// ============================================================
// UNIT ROLLER TOGGLE (Unit 1 / Unit 2 with 3D Drum Peeking)
// ============================================================
const UnitRoller = ({
  id,
  value,
  unit1 = "",
  unit2 = "",
  onChange,
  onKeyDown,
  title,
  height = "100%",
  width = "100%",
  className = "",
}) => {
  const u1 = String(unit1 || "").trim();
  const u2 = String(unit2 || "").trim();
  const hasTwoUnits = Boolean(
    u1 && u2 && u1.toLowerCase() !== u2.toLowerCase(),
  );

  // Check if unit2 is currently active
  const isUnit2 = Boolean(
    hasTwoUnits &&
    value &&
    String(value).trim().toLowerCase() === u2.toLowerCase(),
  );

  const displayVal = value || u1 || u2 || "-";
  const buttonRef = useRef(null);

  useEffect(() => {
    const el = buttonRef.current;
    if (!el || !hasTwoUnits) return;

    const onWheelHandler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.deltaY < 0) {
        onChange(u1);
      } else if (e.deltaY > 0) {
        onChange(u2);
      }
    };

    el.addEventListener("wheel", onWheelHandler, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheelHandler);
    };
  }, [hasTwoUnits, u1, u2, onChange]);

  const handleToggle = () => {
    if (!hasTwoUnits) return;
    const nextVal = isUnit2 ? u1 : u2;
    onChange(nextVal);
  };

  const handleKeyDownInternal = (e) => {
    if (hasTwoUnits) {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        onChange(u1);
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        onChange(u2);
        return;
      }
      if (e.key === "1") {
        e.preventDefault();
        onChange(u1);
        return;
      }
      if (e.key === "2") {
        e.preventDefault();
        onChange(u2);
        return;
      }
      if (e.key === " ") {
        e.preventDefault();
        handleToggle();
        return;
      }
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // If only 1 unit exists or no unit specified
  if (!hasTwoUnits) {
    return (
      <button
        ref={buttonRef}
        id={id}
        type="button"
        tabIndex={0}
        className={`receipt-grid-roller-btn ${className}`.trim()}
        title={title || (displayVal !== "-" ? `Unit: ${displayVal}` : "Unit")}
        onKeyDown={handleKeyDownInternal}
        style={{
          width,
          height,
          minHeight: "38px",
          padding: "0 6px",
          position: "relative",
          overflow: "hidden",
          border: "none",
          outline: "none",
          borderRadius: 0,
          background: "transparent",
          cursor: "default",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          userSelect: "none",
          boxSizing: "border-box",
          fontFamily: "inherit",
          fontSize: "15.5px",
          fontWeight: 800,
          color: displayVal !== "-" ? THEME.text : "#94A3B8",
        }}
      >
        <span
          style={{
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayVal}
        </span>
      </button>
    );
  }

  // 3D vertical sliding drum roller between Unit 1 and Unit 2
  return (
    <button
      ref={buttonRef}
      id={id}
      type="button"
      tabIndex={0}
      className={`receipt-grid-roller-btn ${className}`.trim()}
      title={
        title ||
        `Active: ${isUnit2 ? u2 : u1} (↑ for ${u1}, ↓ for ${u2}, Click/Space to toggle, or Scroll)`
      }
      onClick={handleToggle}
      onKeyDown={handleKeyDownInternal}
      style={{
        width,
        height,
        minHeight: "38px",
        padding: 0,
        position: "relative",
        overflow: "hidden",
        border: "none",
        outline: "none",
        borderRadius: 0,
        background: "transparent",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        userSelect: "none",
        boxSizing: "border-box",
      }}
    >
      {/* Vertical sliding drum track */}
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          transform: !isUnit2 ? "translateY(25%)" : "translateY(-25%)",
          transition: "transform 0.22s cubic-bezier(0.16, 1, 0.3, 1)",
          willChange: "transform",
          pointerEvents: "none",
        }}
      >
        {/* Unit 1 slot (Top half) */}
        <div
          style={{
            height: "50%",
            width: "100%",
            display: "flex",
            alignItems: !isUnit2 ? "center" : "flex-end",
            justifyContent: "center",
            padding: "0 6px",
            paddingBottom: !isUnit2 ? 0 : "2px",
            fontSize: !isUnit2 ? "13px" : "10px",
            fontWeight: !isUnit2 ? 800 : 600,
            color: !isUnit2 ? THEME.primary : "#94A3B8",
            opacity: !isUnit2 ? 1 : 0.35,
            transition: "all 0.2s ease",
            lineHeight: 1,
            userSelect: "none",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {u1}
        </div>

        {/* Unit 2 slot (Bottom half) */}
        <div
          style={{
            height: "50%",
            width: "100%",
            display: "flex",
            alignItems: isUnit2 ? "center" : "flex-start",
            justifyContent: "center",
            padding: "0 6px",
            paddingTop: isUnit2 ? 0 : "2px",
            fontSize: isUnit2 ? "13px" : "10px",
            fontWeight: isUnit2 ? 800 : 600,
            color: isUnit2 ? THEME.primary : "#94A3B8",
            opacity: isUnit2 ? 1 : 0.35,
            transition: "all 0.2s ease",
            lineHeight: 1,
            userSelect: "none",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {u2}
        </div>
      </div>
    </button>
  );
};

const DEFAULT_CALC_SETTINGS = {
  discount2Percent: 0,
  discount1Rs: 0,
  discount2Rs: 0,
  billDiscount2Percent: 0,
};

const Receipt = () => {
  const [suppliers, setSuppliers] = useState(INITIAL_SUPPLIERS);

  // Calculation Settings State (from Organization Settings)
  const [calcSettings, setCalcSettings] = useState(() => {
    try {
      const orgId = localStorage.getItem("orgId");
      const cached =
        localStorage.getItem(`org_calculation_settings_${orgId}`) ||
        localStorage.getItem("org_calculation_settings");
      if (cached) {
        const parsed = JSON.parse(cached);
        return {
          discount2Percent: Number(parsed.discount2Percent) === 1 ? 1 : 0,
          discount1Rs: Number(parsed.discount1Rs) === 1 ? 1 : 0,
          discount2Rs: Number(parsed.discount2Rs) === 1 ? 1 : 0,
          billDiscount2Percent:
            Number(parsed.billDiscount2Percent) === 1 ? 1 : 0,
        };
      }
    } catch (e) {
      // ignore
    }
    return DEFAULT_CALC_SETTINGS;
  });

  // Header State
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [receiptNo, setReceiptNo] = useState("");
  const [receiptDate, setReceiptDate] = useState("");
  const [itemReceivedDate, setItemReceivedDate] = useState("");

  // Line Items State
  const [items, setItems] = useState([]);

  // Footer / Summary State
  const [schemeDisc, setSchemeDisc] = useState(0);
  const [schemeDiscType, setSchemeDiscType] = useState("%");
  const [billDisc, setBillDisc] = useState(0);
  const [billDiscType, setBillDiscType] = useState("Rs");

  // Payment Accounts State (Bottom Save Bill Section)
  const [accountOptions, setAccountOptions] = useState(INITIAL_ACCOUNTS);
  const [account1, setAccount1] = useState("");
  const [amount1, setAmount1] = useState("");
  const [account2, setAccount2] = useState("");
  const [amount2, setAmount2] = useState("");
  const amount1Ref = useRef(null);
  const amount2Ref = useRef(null);
  const [savingBill, setSavingBill] = useState(false);
  const savingBillRef = useRef(false);

  // UI state
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const [isRowFocused, setIsRowFocused] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);

  // Supplier Search Popup State & Refs
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [highlightedSupplierIndex, setHighlightedSupplierIndex] = useState(0);
  const supplierSearchInputRef = useRef(null);
  const supplierListContainerRef = useRef(null);
  const justClosedModalRef = useRef(false);

  // Add Supplier / Create Ledger In-Modal State (Marg ERP Format)
  const [showAddSupplierView, setShowAddSupplierView] = useState(false);
  const [savingNewSupplier, setSavingNewSupplier] = useState(false);
  const [groups, setGroups] = useState([]);
  const supplierFormRef = useRef(null);
  const saveSupplierBtnRef = useRef(null);
  const [newSupplierData, setNewSupplierData] = useState({
    name: "",
    station: "",
    accountGroup: "",
    balancingMethod: "Bill by Bill",
    openingBalance: "0.00",
    openingType: "Cr",
    mailTo: "",
    address: "",
    pinCode: "",
    email: "",
    website: "",
    contactPerson: "",
    designation: "",
    phoneOff: "",
    phoneRes: "",
    mobile: "",
    dlNo: "",
    dlExp: "",
    gstHeading: "Local",
    gstin: "",
    gstinDate: "",
    foodHeading: "Food Licence",
    foodLicenceNo: "",
    foodExp: "",
    extraHeading: "",
    extraNo: "",
    extraExp: "",
    panNo: "",
    ledgerDate: new Date().toISOString().slice(0, 10),
    state: "18-ASSAM",
    ledgerType: "REGISTERED",
  });

  const handleResetNewSupplier = useCallback(() => {
    setNewSupplierData({
      name: "",
      station: "",
      accountGroup: "SUNDRY CREDITORS",
      balancingMethod: "Bill by Bill",
      openingBalance: "0.00",
      openingType: "Cr",
      mailTo: "",
      address: "",
      pinCode: "",
      email: "",
      website: "",
      contactPerson: "",
      designation: "",
      phoneOff: "",
      phoneRes: "",
      mobile: "",
      dlNo: "",
      dlExp: "",
      gstHeading: "Local",
      gstin: "",
      gstinDate: "",
      foodHeading: "Food Licence",
      foodLicenceNo: "",
      foodExp: "",
      extraHeading: "",
      extraNo: "",
      extraExp: "",
      panNo: "",
      ledgerDate: new Date().toISOString().slice(0, 10),
      state: "18-ASSAM",
      ledgerType: "REGISTERED",
    });
  }, []);

  const handleNewSupplierChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewSupplierData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };
      if (name === "name") {
        if (!prev.mailTo || prev.mailTo === prev.name) {
          updated.mailTo = value;
        }
      }
      return updated;
    });
  }, []);

  const filteredSuppliers = useMemo(() => {
    if (!supplierSearch.trim()) return suppliers;
    const q = supplierSearch.toLowerCase().trim();
    return suppliers.filter((s) => {
      if (!s) return false;
      return (
        (s.name && s.name.toLowerCase().includes(q)) ||
        (s.station && s.station.toLowerCase().includes(q)) ||
        (s.address && s.address.toLowerCase().includes(q))
      );
    });
  }, [suppliers, supplierSearch]);

  // Product Search Popup State & Refs (API Driven)
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [activeProductRowIndex, setActiveProductRowIndex] = useState(null);
  const [productSearchText, setProductSearchText] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [highlightedProductIndex, setHighlightedProductIndex] = useState(0);
  const productSearchInputRef = useRef(null);
  const productListContainerRef = useRef(null);
  const justClosedProductModalRef = useRef(false);
  const isArrowNavigatingRef = useRef(false);
  const appendedRowViaEnterRef = useRef(null);

  // Add Product In-Modal State
  const [showAddProductView, setShowAddProductView] = useState(false);
  const [newProductData, setNewProductData] = useState({
    name: "",
    unit1: "",
    unit2: "",
    conversionQty: "",
    primaryUnit: "unit1",
    tax: "",
    allowDecimal: "No",
  });
  const [savingNewProduct, setSavingNewProduct] = useState(false);

  const handleResetNewProduct = useCallback(() => {
    setNewProductData({
      name: "",
      unit1: "",
      unit2: "",
      conversionQty: "",
      primaryUnit: "unit1",
      tax: "",
      allowDecimal: "No",
    });
  }, []);

  const handleNewProductChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewProductData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };
      if (name === "unit2") {
        if (!value.trim()) {
          updated.conversionQty = "1";
          updated.primaryUnit = "unit1";
        } else if (prev.unit2.trim() === "") {
          updated.conversionQty = "";
        }
      }
      return updated;
    });
  }, []);

  const getProducts = useCallback(
    async (searchText = "", page = 1, limit = 20) => {
      try {
        setLoadingProducts(true);
        const queryParams = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        });
        if (searchText && searchText.trim()) {
          queryParams.append("search", searchText.trim());
        }

        const token =
          localStorage.getItem("accessToken") || localStorage.getItem("token");

        const response = await fetch(
          `${BASE_URL}api/user/products?${queryParams.toString()}`,
          {
            method: "GET",
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              "Content-Type": "application/json",
            },
          },
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || "Failed to fetch products");
        }
        setProductResults(data.data || []);
      } catch (error) {
        console.error("Get all products error:", error);
        setProductResults([]);
      } finally {
        setLoadingProducts(false);
      }
    },
    [],
  );

  // Fetch account options from backend or fallback to initial accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const token =
          localStorage.getItem("accessToken") || localStorage.getItem("token");
        const res = await fetch(
          `${BASE_URL}api/user/common/accounts/search?cashSale=1`,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          },
        );
        const data = await res.json();
        if (
          data.success &&
          Array.isArray(data.accounts) &&
          data.accounts.length > 0
        ) {
          setAccountOptions(data.accounts);
        }
      } catch (err) {
        // Fallback already set to INITIAL_ACCOUNTS
      }
    };
    fetchAccounts();
  }, []);

  // Fetch suppliers/ledgers dynamically from backend
  const fetchSuppliers = useCallback(async (query = "") => {
    try {
      const token =
        localStorage.getItem("accessToken") || localStorage.getItem("token");
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      // 1. Try GET api/user/suppliers/
      const res = await fetch(
        `${BASE_URL}api/user/suppliers/${query ? `?search=${encodeURIComponent(query)}` : ""}`,
        { headers },
      );
      if (res.ok) {
        const resData = await res.json();
        const list = Array.isArray(resData)
          ? resData
          : resData?.data || resData?.suppliers || resData?.accounts;
        if (Array.isArray(list) && list.length > 0) {
          setSuppliers(list);
          return;
        }
      }
    } catch (err) {
      console.warn("Fetch suppliers error:", err);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  useEffect(() => {
    const fetchAllGroups = async () => {
      try {
        const token =
          localStorage.getItem("accessToken") || localStorage.getItem("token");
        const res = await fetch(`${BASE_URL}api/user/common/groups/all`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const data = await res.json();

        if (data.success && Array.isArray(data.groups)) {
          setGroups(data.groups);
          const matchingCreditorGroup = data.groups.find(
            (g) =>
              (g?.name || g || "").toString().toUpperCase() ===
              "SUNDRY CREDITORS",
          );
          if (matchingCreditorGroup) {
            const exactName =
              matchingCreditorGroup?.name || matchingCreditorGroup;
            setNewSupplierData((prev) => ({
              ...prev,
              accountGroup: exactName,
            }));
          }
        }
      } catch (err) {
        console.error("Fetch all groups error:", err);
      }
    };

    fetchAllGroups();
  }, []);

  // Fetch organization calculation settings from backend
  useEffect(() => {
    const fetchCalcSettings = async () => {
      try {
        const token =
          localStorage.getItem("accessToken") || localStorage.getItem("token");
        const orgId = localStorage.getItem("orgId");
        const url = `${BASE_URL}api/user/common/calculation-settings`;

        const res = await fetch(url, {
          method: "GET",
          credentials: "include",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
          },
        });

        if (res.ok) {
          const resData = await res.json();
          const data = resData.data || resData.settings || resData;
          if (data && typeof data === "object") {
            const newSettings = {
              discount2Percent: Number(data.discount2Percent) === 1 ? 1 : 0,
              discount1Rs: Number(data.discount1Rs) === 1 ? 1 : 0,
              discount2Rs: Number(data.discount2Rs) === 1 ? 1 : 0,
              billDiscount2Percent:
                Number(data.billDiscount2Percent) === 1 ? 1 : 0,
            };
            setCalcSettings(newSettings);
            if (orgId) {
              localStorage.setItem(
                `org_calculation_settings_${orgId}`,
                JSON.stringify(newSettings),
              );
            }
            localStorage.setItem(
              "org_calculation_settings",
              JSON.stringify(newSettings),
            );
          }
        }
      } catch (err) {
        console.warn("Fetch calculation settings error:", err);
      }
    };

    fetchCalcSettings();
  }, []);

  // Calculations
  const calculatedItems = useMemo(() => {
    return items.map((item) => {
      const qty = Number(item.qty ?? item.quantity) || 0;
      const rate = Number(item.rate ?? item.unitRate) || 0;
      const baseAmount = qty * rate;

      // Discount 1
      let d1Value = 0;
      const d1 = Number(item.disc1 ?? item.discount1Percent) || 0;
      if (item.disc1Type === "%") {
        d1Value = baseAmount * (d1 / 100);
      } else {
        // discount1Rs: 0 = Qty X D1, 1 = Before Tax on Amount (flat)
        d1Value = calcSettings.discount1Rs === 0 ? qty * d1 : d1;
      }

      const afterDisc1 = Math.max(0, baseAmount - d1Value);

      // Discount 2
      let d2Value = 0;
      const d2 = Number(item.disc2 ?? item.discount2Percent) || 0;
      if (item.disc2Type === "%") {
        // discount2Percent: 0 = Qty X Rate X Dis 2%, 1 = Qty X Rate X (100-Dis 1)% X Dis 2%
        const baseForD2 =
          calcSettings.discount2Percent === 0 ? baseAmount : afterDisc1;
        d2Value = baseForD2 * (d2 / 100);
      } else {
        // discount2Rs: 0 = Qty X D2, 1 = Before Tax on Amount (flat)
        d2Value = calcSettings.discount2Rs === 0 ? qty * d2 : d2;
      }

      const totalItemDiscount = d1Value + d2Value;
      const amountBeforeTax = Math.max(0, baseAmount - totalItemDiscount);
      const taxRate = Number(item.tax ?? item.taxPercent) || 0;
      const taxAmount = amountBeforeTax * (taxRate / 100);
      const amountAfterTax = amountBeforeTax + taxAmount;

      return {
        ...item,
        qty:
          item.qty !== undefined && item.qty !== ""
            ? item.qty
            : (item.quantity ?? 1),
        quantity: qty,
        rate:
          item.rate !== undefined && item.rate !== ""
            ? item.rate
            : (item.unitRate ?? ""),
        unitRate: rate,
        tax:
          item.tax !== undefined && item.tax !== ""
            ? item.tax
            : (item.taxPercent ?? ""),
        taxPercent: taxRate,
        disc1:
          item.disc1 !== undefined && item.disc1 !== ""
            ? item.disc1
            : (item.discount1Percent ?? 0),
        discount1Percent: d1,
        disc2:
          item.disc2 !== undefined && item.disc2 !== ""
            ? item.disc2
            : (item.discount2Percent ?? 0),
        discount2Percent: d2,
        baseAmount,
        totalDisc: totalItemDiscount,
        amountBeforeTax,
        beforeTax: amountBeforeTax,
        taxAmount,
        amountAfterTax,
        netAmount: amountAfterTax,
      };
    });
  }, [items, calcSettings]);

  // Sub Total = all product's sum of rate * qty
  const subTotal = calculatedItems.reduce(
    (acc, curr) =>
      acc +
      Number(curr.quantity ?? curr.qty ?? 0) *
        Number(curr.unitRate ?? curr.rate ?? 0),
    0,
  );

  const grandTotal = calculatedItems.reduce(
    (acc, curr) => acc + curr.amountBeforeTax,
    0,
  );
  const totalItemDisc = calculatedItems.reduce(
    (acc, curr) => acc + curr.totalDisc,
    0,
  );

  // Scheme discount calc (Bill Discount 1)
  const schemeDiscCalculated =
    schemeDiscType === "%"
      ? grandTotal * (Number(schemeDisc) / 100)
      : Number(schemeDisc);

  const grandTotalAfterScheme = Math.max(0, grandTotal - schemeDiscCalculated);

  // Bill level discount calc (Bill Discount 2)
  // billDiscount2Percent: 0 = Net Bill Amount X BD 2 (%), 1 = {Net Bill Amount - (100-BD 1)%} X BD 2 %
  const billDiscCalculated =
    billDiscType === "%"
      ? (calcSettings.billDiscount2Percent === 0
          ? grandTotal
          : grandTotalAfterScheme) *
        (Number(billDisc) / 100)
      : Number(billDisc);

  const totalTaxAmount = calculatedItems.reduce(
    (acc, curr) => acc + curr.taxAmount,
    0,
  );
  const grandTotalBeforeBillDisc = grandTotalAfterScheme + totalTaxAmount;
  const netPayment = Math.max(0, grandTotalBeforeBillDisc - billDiscCalculated);

  const ensureElementVisible = useCallback((element) => {
    if (!element) return;
    const container =
      element.closest(".receipt-table-scroll") ||
      document.querySelector(".receipt-table-scroll");
    if (!container) return;

    const thead = container.querySelector("thead");
    const headerHeight = thead ? thead.offsetHeight : 38;

    const targetRow = element.closest("tr") || element;
    const rowRect = targetRow.getBoundingClientRect();
    const elRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // The visible top boundary is just below the sticky table header
    const visibleTop = containerRect.top + headerHeight;
    const visibleBottom = containerRect.bottom;

    if (rowRect.top < visibleTop) {
      // Row is partially or completely hidden behind or above sticky thead
      const diff = visibleTop - rowRect.top;
      container.scrollTop = Math.max(0, container.scrollTop - diff - 4);
    } else if (rowRect.bottom > visibleBottom) {
      // Row is partially or completely below visible bottom of container
      const diff = rowRect.bottom - visibleBottom;
      container.scrollTop += diff + 4;
    }

    // Horizontal visibility check so active cell is in view
    const visibleLeft = containerRect.left;
    const visibleRight = containerRect.right;
    if (elRect.left < visibleLeft) {
      container.scrollLeft = Math.max(
        0,
        container.scrollLeft - (visibleLeft - elRect.left + 4),
      );
    } else if (elRect.right > visibleRight) {
      container.scrollLeft += elRect.right - visibleRight + 4;
    }
  }, []);

  const addNewRow = useCallback(
    (options = {}) => {
      const { isFromLastCellEnter = false, aboveRowIndex = null } = options;
      const nextIndex = items.length;
      const newItemId = Date.now() + Math.random();
      const newItem = {
        id: newItemId,
        product: null,
        pack: "",
        batch: "",
        batchNo: "",
        qty: 1,
        quantity: 1,
        unit: "",
        rate: "",
        unitRate: "",
        mrp: "",
        tax: "",
        taxPercent: "",
        disc1: 0,
        discount1Percent: 0,
        disc1Type: "%",
        disc2: 0,
        discount2Percent: 0,
        disc2Type: "Rs",
        manufacturingDate: "",
        expiryDate: "",
      };

      if (isFromLastCellEnter && aboveRowIndex !== null) {
        appendedRowViaEnterRef.current = {
          id: newItemId,
          aboveRowIndex,
        };
      } else {
        appendedRowViaEnterRef.current = null;
      }

      setItems((prev) => [...prev, newItem]);
      setSelectedRowIndex(nextIndex);
      setIsRowFocused(false);

      setTimeout(() => {
        const el = document.getElementById(`row-${nextIndex}-product`);
        if (el) {
          el.focus({ preventScroll: true });
          ensureElementVisible(el);
        }
      }, 60);
    },
    [items.length, ensureElementVisible],
  );

  const handleLastCellEnter = useCallback(
    (index) => {
      if (index < items.length - 1) {
        setSelectedRowIndex(index + 1);
        setIsRowFocused(false);
        const nextEl = document.getElementById(`row-${index + 1}-product`);
        if (nextEl) {
          nextEl.focus({ preventScroll: true });
          ensureElementVisible(nextEl);
        } else {
          setTimeout(() => {
            const delayedNextEl = document.getElementById(
              `row-${index + 1}-product`,
            );
            delayedNextEl?.focus({ preventScroll: true });
            if (delayedNextEl) ensureElementVisible(delayedNextEl);
          }, 50);
        }
      } else {
        addNewRow({ isFromLastCellEnter: true, aboveRowIndex: index });
      }
    },
    [items.length, addNewRow, ensureElementVisible],
  );

  const removeRow = useCallback(
    (index, colName) => {
      const activeId = document.activeElement?.id;
      let targetCol = colName;
      if (!targetCol && activeId) {
        const parts = activeId.split("-");
        if (parts.length >= 3 && parts[0] === "row") {
          targetCol = parts.slice(2).join("-");
        }
      }
      if (!targetCol) targetCol = "product";

      setItems((prev) => {
        const updated = prev.filter((_, i) => i !== index);
        if (updated.length === 0) {
          setSelectedRowIndex(0);
          setSnackbar({
            open: true,
            message: "Row deleted. Table is empty.",
            severity: "info",
          });
          return updated;
        }
        const targetIdx = Math.min(index, updated.length - 1);
        setSelectedRowIndex(targetIdx);
        setTimeout(() => {
          isArrowNavigatingRef.current = true;
          const nextCell =
            document.getElementById(`row-${targetIdx}-${targetCol}`) ||
            document.getElementById(`row-${targetIdx}-product`) ||
            document.getElementById(`row-${targetIdx}-delete`);
          if (nextCell) {
            nextCell.focus({ preventScroll: true });
            if (
              typeof nextCell.select === "function" &&
              nextCell.tagName === "INPUT" &&
              nextCell.type !== "button"
            ) {
              nextCell.select();
            }
            ensureElementVisible(nextCell);
          }
          setTimeout(() => {
            isArrowNavigatingRef.current = false;
          }, 120);
        }, 50);
        return updated;
      });
    },
    [ensureElementVisible],
  );

  const GRID_COLUMNS = useMemo(
    () => [
      "product",
      "batch",
      "qty",
      "unit",
      "rate",
      "disc1",
      "disc1-type",
      "disc2",
      "disc2-type",
      "tax",
      "delete",
    ],
    [],
  );

  const focusCell = useCallback(
    (rowIndex, colName) => {
      isArrowNavigatingRef.current = true;
      setSelectedRowIndex(rowIndex);
      setIsRowFocused(false);
      const el = document.getElementById(`row-${rowIndex}-${colName}`);
      if (el) {
        el.focus({ preventScroll: true });
        if (
          typeof el.select === "function" &&
          el.tagName === "INPUT" &&
          el.type !== "button"
        ) {
          el.select();
        }
        ensureElementVisible(el);
        setTimeout(() => {
          isArrowNavigatingRef.current = false;
        }, 120);
      } else {
        setTimeout(() => {
          const delayedEl = document.getElementById(
            `row-${rowIndex}-${colName}`,
          );
          if (delayedEl) {
            delayedEl.focus({ preventScroll: true });
            if (
              typeof delayedEl.select === "function" &&
              delayedEl.tagName === "INPUT" &&
              delayedEl.type !== "button"
            ) {
              delayedEl.select();
            }
            ensureElementVisible(delayedEl);
          }
          setTimeout(() => {
            isArrowNavigatingRef.current = false;
          }, 120);
        }, 20);
      }
    },
    [ensureElementVisible],
  );

  const focusRow = useCallback(
    (rowIndex) => {
      if (items.length === 0) return;
      const clampedIndex = Math.max(0, Math.min(rowIndex, items.length - 1));
      setSelectedRowIndex(clampedIndex);
      setIsRowFocused(true);
      const rowEl = document.getElementById(`receipt-row-${clampedIndex}`);
      if (rowEl) {
        rowEl.focus({ preventScroll: true });
        ensureElementVisible(rowEl);
      } else {
        setTimeout(() => {
          const delayedRowEl = document.getElementById(
            `receipt-row-${clampedIndex}`,
          );
          if (delayedRowEl) {
            delayedRowEl.focus({ preventScroll: true });
            ensureElementVisible(delayedRowEl);
          }
        }, 20);
      }
    },
    [items.length, ensureElementVisible],
  );

  const handleGridArrowNav = useCallback(
    (e, rowIndex, colName) => {
      // Shift + Backspace on any table cell deletes that row
      if (e.shiftKey && e.key === "Backspace") {
        e.preventDefault();
        removeRow(rowIndex, colName);
        return true;
      }

      // Escape returns focus to the whole row
      if (e.key === "Escape") {
        e.preventDefault();
        focusRow(rowIndex);
        return true;
      }

      const colIdx = GRID_COLUMNS.indexOf(colName);
      if (colIdx === -1) return false;

      // On Up/Down in the table: make full focus on the row not on column
      if (e.key === "ArrowUp") {
        e.preventDefault();
        focusRow(rowIndex > 0 ? rowIndex - 1 : 0);
        return true;
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        focusRow(rowIndex < items.length - 1 ? rowIndex + 1 : rowIndex);
        return true;
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (colIdx > 0) {
          focusCell(rowIndex, GRID_COLUMNS[colIdx - 1]);
        }
        return true;
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (colIdx < GRID_COLUMNS.length - 1) {
          focusCell(rowIndex, GRID_COLUMNS[colIdx + 1]);
        }
        return true;
      }
      return false;
    },
    [GRID_COLUMNS, items.length, focusCell, focusRow, removeRow],
  );

  const handleRowKeyDown = useCallback(
    (e, index) => {
      if (e.target !== e.currentTarget) return;

      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (index > 0) {
          focusRow(index - 1);
        }
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        if (index < items.length - 1) {
          focusRow(index + 1);
        }
      } else if (e.key === "Enter" || e.key === "ArrowRight") {
        e.preventDefault();
        // On enter to that row, focus to the first column
        focusCell(index, "product");
      } else if (e.shiftKey && e.key === "Backspace") {
        e.preventDefault();
        removeRow(index);
      }
    },
    [focusCell, focusRow, items.length, removeRow],
  );

  const updateItemField = useCallback((index, field, value) => {
    setItems((prev) => {
      const updated = [...prev];
      if (!updated[index]) return prev;

      const currentRow = { ...updated[index], [field]: value };

      // Keep alias fields synchronized
      if (field === "batchNo" || field === "batch") {
        currentRow.batchNo = value;
        currentRow.batch = value;
      } else if (field === "qty" || field === "quantity") {
        currentRow.qty = value;
        currentRow.quantity = value;
      } else if (field === "rate" || field === "unitRate") {
        currentRow.rate = value;
        currentRow.unitRate = value;
      } else if (field === "tax" || field === "taxPercent") {
        currentRow.tax = value;
        currentRow.taxPercent = value;
      } else if (field === "disc1" || field === "discount1Percent") {
        currentRow.disc1 = value;
        currentRow.discount1Percent = value;
      } else if (field === "disc2" || field === "discount2Percent") {
        currentRow.disc2 = value;
        currentRow.discount2Percent = value;
      }

      // If product changes, auto populate details from API product object
      if (field === "product" && value) {
        let chosenUnit = "";
        if (
          value.primaryUnit === "unit2" &&
          value.unit2 &&
          String(value.unit2).trim()
        ) {
          chosenUnit = String(value.unit2).trim();
        } else if (value.unit1 && String(value.unit1).trim()) {
          chosenUnit = String(value.unit1).trim();
        } else if (value.unit && String(value.unit).trim()) {
          chosenUnit = String(value.unit).trim();
        } else if (value.unit2 && String(value.unit2).trim()) {
          chosenUnit = String(value.unit2).trim();
        }
        currentRow.unit = chosenUnit;

        const taxVal =
          value.tax !== undefined && value.tax !== null
            ? String(value.tax)
            : "";
        currentRow.tax = taxVal;
        currentRow.taxPercent = taxVal;

        const rateVal =
          value.pRate ??
          value.purchaseRate ??
          value.rate ??
          value.sRate ??
          value.mrp ??
          "";
        currentRow.rate = rateVal;
        currentRow.unitRate = rateVal;

        const mrpVal = value.mrp ?? rateVal ?? "";
        currentRow.mrp = mrpVal;

        currentRow.pack = value.pack || value.package || "";

        const batchVal = value.batchNo || value.batch || "";
        currentRow.batch = batchVal;
        currentRow.batchNo = batchVal;

        if (value.manufacturingDate) {
          currentRow.manufacturingDate = value.manufacturingDate;
        }
        if (value.expiryDate) {
          currentRow.expiryDate = value.expiryDate;
        }
      }

      updated[index] = currentRow;
      return updated;
    });
  }, []);

  const resetReceiptForm = useCallback(() => {
    setSelectedSupplier(null);
    setReceiptNo("");
    setReceiptDate("");
    setItemReceivedDate("");
    setItems([]);
    setSchemeDisc(0);
    setSchemeDiscType("%");
    setBillDisc(0);
    setBillDiscType("Rs");
    setAccount1("");
    setAmount1("");
    setAccount2("");
    setAmount2("");
    setSelectedRowIndex(0);
  }, []);

  const handleSaveBill = useCallback(async () => {
    if (savingBillRef.current) return;

    const supplierPartyId = selectedSupplier?._id || selectedSupplier?.id;
    if (!selectedSupplier || !supplierPartyId) {
      setSnackbar({
        open: true,
        message: "Supplier / Party Name is required. Please select a supplier.",
        severity: "warning",
      });
      document.getElementById("supplier-input")?.focus();
      return;
    }

    const isValidObjectId = (id) =>
      typeof id === "string" && /^[0-9a-fA-F]{24}$/.test(id.trim());

    if (!isValidObjectId(String(supplierPartyId))) {
      setSnackbar({
        open: true,
        message: "Invalid Supplier / Party ID. Please select a valid supplier.",
        severity: "error",
      });
      document.getElementById("supplier-input")?.focus();
      return;
    }

    if (!receiptNo?.trim()) {
      setSnackbar({
        open: true,
        message: "Receipt / Bill No. is required.",
        severity: "warning",
      });
      document.getElementById("receipt-no-input")?.focus();
      return;
    }

    if (!receiptDate?.trim()) {
      setSnackbar({
        open: true,
        message: "Receipt Date is required.",
        severity: "warning",
      });
      document.getElementById("receipt-date-input")?.focus();
      return;
    }

    if (!itemReceivedDate?.trim()) {
      setSnackbar({
        open: true,
        message: "Item Received Date is required.",
        severity: "warning",
      });
      document.getElementById("item-received-date-input")?.focus();
      return;
    }

    const billDateStr = receiptDate.trim();
    const entryDateStr = itemReceivedDate.trim();

    if (!calculatedItems || calculatedItems.length === 0) {
      setSnackbar({
        open: true,
        message: "At least one product item is required in the items grid.",
        severity: "warning",
      });
      addNewRow();
      return;
    }

    // Validate item inputs matching controller and required fields
    for (let i = 0; i < calculatedItems.length; i++) {
      const it = calculatedItems[i];
      const prodId = it.product?._id || it.product?.id;
      const prodName = it.product?.name?.trim() || "";

      // 1. PRODUCT NAME validation
      if (
        !it.product ||
        !prodId ||
        !isValidObjectId(String(prodId)) ||
        !prodName
      ) {
        setSnackbar({
          open: true,
          message: `Row #${i + 1}: Product Name is required. Please select a product.`,
          severity: "warning",
        });
        focusCell(i, "product");
        return;
      }

      // 2. BATCH NO. validation
      const bNo = (it.batchNo || it.batch || "").trim();
      if (!bNo) {
        setSnackbar({
          open: true,
          message: `Row #${i + 1} (${prodName}): Batch number is required.`,
          severity: "warning",
        });
        focusCell(i, "batch");
        return;
      }

      // 3. QTY validation
      const rawQty =
        it.quantity !== undefined && it.quantity !== null && it.quantity !== ""
          ? it.quantity
          : it.qty;
      const qVal = Number(rawQty);
      if (
        rawQty === "" ||
        rawQty === null ||
        rawQty === undefined ||
        isNaN(qVal) ||
        qVal <= 0
      ) {
        setSnackbar({
          open: true,
          message: `Row #${i + 1} (${prodName}): Quantity is required and must be greater than 0.`,
          severity: "warning",
        });
        focusCell(i, "qty");
        return;
      }

      // 4. UNIT validation
      const itemUnit = (
        it.unit ||
        it.product?.unit1 ||
        it.product?.primaryUnit ||
        it.product?.unit ||
        ""
      ).trim();
      if (!itemUnit) {
        setSnackbar({
          open: true,
          message: `Row #${i + 1} (${prodName}): Unit is required.`,
          severity: "warning",
        });
        focusCell(i, "unit");
        return;
      }

      // 5. RATE (₹) validation
      const rawRate =
        it.rate !== undefined && it.rate !== null && it.rate !== ""
          ? it.rate
          : it.unitRate;
      const rVal = Number(rawRate);
      if (
        rawRate === "" ||
        rawRate === null ||
        rawRate === undefined ||
        isNaN(rVal) ||
        rVal <= 0
      ) {
        setSnackbar({
          open: true,
          message: `Row #${i + 1} (${prodName}): Rate (₹) is required and must be greater than 0.`,
          severity: "warning",
        });
        focusCell(i, "rate");
        return;
      }
    }

    const validItems = calculatedItems;

    savingBillRef.current = true;
    setSavingBill(true);

    try {
      const token =
        localStorage.getItem("accessToken") || localStorage.getItem("token");

      // Match payment accounts
      const selectedAcc1 = accountOptions.find(
        (a) =>
          (typeof a === "object" &&
            (a._id === account1 || a.id === account1 || a.name === account1)) ||
          (typeof a === "string" && a === account1),
      );
      const acc1Id = selectedAcc1?._id || selectedAcc1?.id || account1;

      const selectedAcc2 = accountOptions.find(
        (a) =>
          (typeof a === "object" &&
            (a._id === account2 || a.id === account2 || a.name === account2)) ||
          (typeof a === "string" && a === account2),
      );
      const acc2Id = selectedAcc2?._id || selectedAcc2?.id || account2;

      const payload = {
        partyId: supplierPartyId,
        supplierId: supplierPartyId,
        billNo: receiptNo.trim(),
        billDate: billDateStr,
        receiptDate: billDateStr,
        entryDate: entryDateStr,
        itemReceivedDate: entryDateStr,
        transactionType: 1, // 1 = Purchase
        billMode: 8,

        items: validItems.map((item) => {
          const qty = Number(item.quantity ?? item.qty ?? 0);
          const rateVal = Number(item.rate ?? item.unitRate ?? 0);
          const grossAmt = Number((qty * rateVal).toFixed(2));

          const d1Val = Number(item.disc1 ?? item.discount1Percent ?? 0);
          const d1Type = item.disc1Type || "%";
          let d1Amt = 0;
          if (d1Type === "%") {
            d1Amt = Number(((grossAmt * d1Val) / 100).toFixed(2));
          } else {
            // discount1Rs: 0 = Qty X D1, 1 = Before Tax on Amount (flat)
            d1Amt = Number(
              (calcSettings.discount1Rs === 0 ? qty * d1Val : d1Val).toFixed(2),
            );
          }

          const baseForD2 = Math.max(0, grossAmt - d1Amt);
          const d2Val = Number(item.disc2 ?? item.discount2Percent ?? 0);
          const d2Type = item.disc2Type || "Rs";
          let d2Amt = 0;
          if (d2Type === "%") {
            // discount2Percent: 0 = Qty X Rate X Dis 2%, 1 = Qty X Rate X (100-Dis 1)% X Dis 2%
            const baseAmtForD2 =
              calcSettings.discount2Percent === 0 ? grossAmt : baseForD2;
            d2Amt = Number(((baseAmtForD2 * d2Val) / 100).toFixed(2));
          } else {
            // discount2Rs: 0 = Qty X D2, 1 = Before Tax on Amount (flat)
            d2Amt = Number(
              (calcSettings.discount2Rs === 0 ? qty * d2Val : d2Val).toFixed(2),
            );
          }

          const totalDisc = Number((d1Amt + d2Amt).toFixed(2));
          const beforeTaxAmt = Math.max(
            0,
            Number((grossAmt - totalDisc).toFixed(2)),
          );
          const taxPct = Number(
            item.taxPercent ?? item.tax ?? item.product?.tax ?? 0,
          );
          const taxAmt = Number(((beforeTaxAmt * taxPct) / 100).toFixed(2));
          const netAmt = Number((beforeTaxAmt + taxAmt).toFixed(2));

          return {
            productId: item.product?._id || item.product?.id,
            batchNo: (item.batchNo || item.batch || "").trim(),
            rate: rateVal,
            unitRate: rateVal,
            quantity: qty,
            unit: (
              item.unit ||
              item.product?.primaryUnit ||
              item.product?.unit1 ||
              "Pcs"
            ).trim(),
            taxPercent: taxPct,
            discount1: d1Val,
            discount1Percent: d1Val,
            discount1Type: d1Type,
            discount1Amount: d1Amt,
            discount2: d2Val,
            discount2Percent: d2Val,
            discount2Type: d2Type,
            discount2Amount: d2Amt,
            totalDiscountAmount: totalDisc,
            beforeTax: beforeTaxAmt,
            taxAmount: taxAmt,
            netAmount: netAmt,
            manufacturingDate: item.manufacturingDate || null,
            expiryDate: item.expiryDate || null,
            mrp: Number(item.mrp || item.product?.mrp || rateVal || 0),
          };
        }),

        subtotal: Number(subTotal.toFixed(2)),

        // Scheme discount / billDiscount1
        schemeDiscount: Number(schemeDisc || 0),
        schemeDiscountType: schemeDiscType || "%",
        schemeDiscountAmount: Number(schemeDiscCalculated.toFixed(2)),
        billDiscount1: Number(schemeDisc || 0),
        billDiscount1Type: schemeDiscType || "%",
        billDiscount1Amount: Number(schemeDiscCalculated.toFixed(2)),

        // Bill discount / billDiscount2
        billDiscount: Number(billDisc || 0),
        billDiscountType: billDiscType || "Rs",
        billDiscountAmount: Number(billDiscCalculated.toFixed(2)),
        billDiscount2: Number(billDisc || 0),
        billDiscount2Type: billDiscType || "Rs",
        billDiscount2Amount: Number(billDiscCalculated.toFixed(2)),

        // Item & Total Discounts
        totalItemDiscount: Number(totalItemDisc.toFixed(2)),
        totalDiscount: Number(
          (totalItemDisc + schemeDiscCalculated + billDiscCalculated).toFixed(
            2,
          ),
        ),

        // Taxes
        totalTax: Number(totalTaxAmount.toFixed(2)),

        // Bill Totals
        billTotal: Number(netPayment.toFixed(2)),
        grandTotal: Number(grandTotal.toFixed(2)),
        netPayment: Number(netPayment.toFixed(2)),
        netPaidAmount: Number(netPayment.toFixed(2)),

        // Payment Accounts
        account1: isValidObjectId(String(acc1Id)) ? String(acc1Id) : "",
        amount1: Number(amount1 || 0),
        account2: isValidObjectId(String(acc2Id)) ? String(acc2Id) : "",
        amount2: Number(amount2 || 0),

        notes: "",
        status: 0,
      };

      const response = await axios.post(
        `${BASE_URL}api/user/purchase`,
        payload,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
          },
        },
      );

      if (
        response.data?.success ||
        response.status === 200 ||
        response.status === 201
      ) {
        setSnackbar({
          open: true,
          message:
            response.data?.message ||
            `Purchase Bill "${receiptNo}" saved successfully!`,
          severity: "success",
        });

        resetReceiptForm();
      }
    } catch (error) {
      console.error("Error saving purchase:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to save purchase bill.",
        severity: "error",
      });
    } finally {
      savingBillRef.current = false;
      setSavingBill(false);
    }
  }, [
    selectedSupplier,
    calculatedItems,
    receiptNo,
    receiptDate,
    itemReceivedDate,
    subTotal,
    schemeDisc,
    schemeDiscType,
    schemeDiscCalculated,
    billDisc,
    billDiscType,
    billDiscCalculated,
    totalItemDisc,
    totalTaxAmount,
    netPayment,
    grandTotal,
    account1,
    amount1,
    account2,
    amount2,
    accountOptions,
    resetReceiptForm,
    focusCell,
    addNewRow,
    calcSettings,
  ]);

  const handleOpenSupplierModal = () => {
    if (justClosedModalRef.current) return;
    setShowAddSupplierView(false);
    setSupplierModalOpen(true);
    fetchSuppliers();
  };

  const handleCloseSupplierModal = useCallback(() => {
    setShowAddSupplierView(false);
    justClosedModalRef.current = true;
    setSupplierModalOpen(false);
    setTimeout(() => {
      justClosedModalRef.current = false;
    }, 200);
  }, []);

  const handleSelectSupplier = useCallback((supplier) => {
    justClosedModalRef.current = true;
    setSelectedSupplier(supplier);
    setShowAddSupplierView(false);
    setSupplierModalOpen(false);
    setTimeout(() => {
      justClosedModalRef.current = false;
      document.getElementById("receipt-no-input")?.focus();
    }, 100);
  }, []);

  const handleCreateSupplier1 = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const trimmedName = newSupplierData.name?.trim();
    if (!trimmedName) {
      setSnackbar({
        open: true,
        message: "Please enter Ledger / Supplier Name",
        severity: "error",
      });
      return;
    }

    setSavingNewSupplier(true);

    try {
      // Find matching group object if available
      const selectedGroupObj = groups.find((g) => {
        const gName = typeof g === "string" ? g : g?.name || g?.groupName;
        return gName === newSupplierData.accountGroup;
      });

      // Construct payload with all supplier form fields
      const payload = {
        ...newSupplierData,
        name: trimmedName,
        displayName: newSupplierData.mailTo?.trim() || trimmedName,
        mailTo: newSupplierData.mailTo?.trim() || trimmedName,
        type: newSupplierData.accountGroup,
        accountGroup: newSupplierData.accountGroup,
        ...(selectedGroupObj?._id ? { groupId: selectedGroupObj._id } : {}),
        station: newSupplierData.station?.trim() || "",
        balancingMethod: newSupplierData.balancingMethod || "Bill by Bill",
        openingBalance: newSupplierData.openingBalance || "0.00",
        openingType: newSupplierData.openingType || "Cr",
        pinCode: newSupplierData.pinCode?.trim() || "",
        address: newSupplierData.address?.trim() || "",
        contactPerson: newSupplierData.contactPerson?.trim() || "",
        designation: newSupplierData.designation?.trim() || "",
        mobile: newSupplierData.mobile?.trim() || "",
        phoneOff: newSupplierData.phoneOff?.trim() || "",
        phoneRes: newSupplierData.phoneRes?.trim() || "",
        phone:
          newSupplierData.mobile?.trim() ||
          newSupplierData.phoneOff?.trim() ||
          "",
        email: newSupplierData.email?.trim() || "",
        website: newSupplierData.website?.trim() || "",
        gstHeading: newSupplierData.gstHeading || "Local",
        gstin: newSupplierData.gstin?.trim() || "",
        gstinDate: newSupplierData.gstinDate || "",
        panNo: newSupplierData.panNo?.trim() || "",
        dlNo: newSupplierData.dlNo?.trim() || "",
        dlExp: newSupplierData.dlExp || "",
        foodHeading: newSupplierData.foodHeading?.trim() || "Food Licence",
        foodLicenceNo: newSupplierData.foodLicenceNo?.trim() || "",
        foodExp: newSupplierData.foodExp || "",
        state: newSupplierData.state || "18-ASSAM",
        ledgerType: newSupplierData.ledgerType || "REGISTERED",
        ledgerDate:
          newSupplierData.ledgerDate || new Date().toISOString().slice(0, 10),
      };

      let apiSupplier = null;
      let resMessage = "";

      // Attempt to save to backend account API if available
      try {
        const token =
          localStorage.getItem("accessToken") || localStorage.getItem("token");
        const response = await fetch(`${BASE_URL}api/user/suppliers/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        });
        const resData = await response.json();
        if (resData && (resData.success || resData.data || resData.account)) {
          apiSupplier = resData.data || resData.account;
          resMessage = resData.message || "";
        }
      } catch (err) {
        console.warn("Backend account create warning:", err);
      }

      // Generate a new supplier code and ID
      const newId =
        apiSupplier?._id ||
        apiSupplier?.id ||
        "S" + Date.now().toString().slice(-4);
      const newCode =
        apiSupplier?.code || (suppliers.length + 1).toString().padStart(3, "0");
      const newType =
        apiSupplier?.type ||
        newSupplierData.gstHeading?.toUpperCase() ||
        (newSupplierData.state?.includes("ASSAM") ? "LOCAL" : "INTERSTATE");

      const createdSupplier = {
        ...payload,
        id: newId,
        _id: newId,
        name: trimmedName,
        code: newCode,
        type: newType,
        station: newSupplierData.station || "",
        ...(apiSupplier || {}),
      };

      // Prepend to suppliers list avoiding duplicates
      setSuppliers((prev) => [
        createdSupplier,
        ...prev.filter(
          (s) =>
            (s._id || s.id) !== (createdSupplier._id || createdSupplier.id),
        ),
      ]);

      // Reset form and view
      setShowAddSupplierView(false);
      handleResetNewSupplier();

      setSnackbar({
        open: true,
        message: resMessage || "Supplier / Ledger created successfully",
        severity: "success",
      });

      // Auto-select the newly created supplier
      handleSelectSupplier(createdSupplier);
    } catch (error) {
      console.error("Create supplier error:", error);
      setSnackbar({
        open: true,
        message:
          error.message || "Failed to create supplier. Please try again.",
        severity: "error",
      });
    } finally {
      setSavingNewSupplier(false);
    }
  };

  const handleCreateSupplier = async (e) => {
    e?.preventDefault();

    const trimmedName = newSupplierData.name?.trim();

    if (!trimmedName) {
      setSnackbar({
        open: true,
        message: "Please enter Ledger / Supplier Name",
        severity: "error",
      });
      return;
    }

    setSavingNewSupplier(true);

    try {
      // Find selected account group
      const selectedGroupObj = groups.find((g) => {
        const gName = typeof g === "string" ? g : g?.name || g?.groupName;

        return gName === newSupplierData.accountGroup;
      });

      // Payload
      const payload = {
        ...newSupplierData,
        name: trimmedName,
        displayName: newSupplierData.mailTo?.trim() || trimmedName,
        mailTo: newSupplierData.mailTo?.trim() || trimmedName,
        accountGroup: newSupplierData.accountGroup,
        station: newSupplierData.station?.trim() || "",
        balancingMethod: newSupplierData.balancingMethod || "",
        openingBalance: newSupplierData.openingBalance || "0.00",
        openingType: newSupplierData.openingType || "Cr",
        pinCode: newSupplierData.pinCode?.trim() || "",
        address: newSupplierData.address?.trim() || "",
        contactPerson: newSupplierData.contactPerson?.trim() || "",
        designation: newSupplierData.designation?.trim() || "",
        mobile: newSupplierData.mobile?.trim() || "",
        phoneOff: newSupplierData.phoneOff?.trim() || "",
        phoneRes: newSupplierData.phoneRes?.trim() || "",
        phone:
          newSupplierData.mobile?.trim() ||
          newSupplierData.phoneOff?.trim() ||
          "",
        email: newSupplierData.email?.trim() || "",
        website: newSupplierData.website?.trim() || "",
        gstHeading: newSupplierData.gstHeading || "Local",
        gstin: newSupplierData.gstin?.trim() || "",
        gstinDate: newSupplierData.gstinDate || "",
        panNo: newSupplierData.panNo?.trim() || "",
        dlNo: newSupplierData.dlNo?.trim() || "",
        dlExp: newSupplierData.dlExp || "",
        foodHeading: newSupplierData.foodHeading?.trim() || "Food Licence",
        foodLicenceNo: newSupplierData.foodLicenceNo?.trim() || "",
        foodExp: newSupplierData.foodExp || "",
        state: newSupplierData.state || "18-ASSAM",
        ledgerType: newSupplierData.ledgerType || "REGISTERED",
        ledgerDate:
          newSupplierData.ledgerDate || new Date().toISOString().slice(0, 10),
      };

      const token =
        localStorage.getItem("accessToken") || localStorage.getItem("token");

      const response = await fetch(`${BASE_URL}api/user/suppliers/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const resData = await response.json();

      // Backend error
      if (!response.ok || !resData.success) {
        throw new Error(resData.message || "Failed to create supplier");
      }

      // ==========================================
      // USE SERVER RESPONSE
      // ==========================================

      const createdSupplier = resData.supplier;
      const createdAccount = resData.account;

      console.log("Created Supplier:", createdSupplier);
      console.log("Created Account:", createdAccount);

      // ==========================================
      // Add SERVER supplier to React state
      // ==========================================

      setSuppliers((prev) => [
        createdSupplier,
        ...prev.filter(
          (s) =>
            (s._id || s.id) !== (createdSupplier._id || createdSupplier.id),
        ),
      ]);

      // Reset form
      setShowAddSupplierView(false);
      handleResetNewSupplier();

      // Success message from server
      setSnackbar({
        open: true,
        message: resData.message || "Supplier / Ledger created successfully",
        severity: "success",
      });

      // Select the actual SERVER-created supplier
      handleSelectSupplier(createdSupplier);
    } catch (error) {
      console.error("Create supplier error:", error);

      setSnackbar({
        open: true,
        message:
          error.message || "Failed to create supplier. Please try again.",
        severity: "error",
      });
    } finally {
      setSavingNewSupplier(false);
    }
  };
  const lastOpenedSelectRef = useRef(null);

  const openSelectPicker = useCallback((el) => {
    if (!el || (el.tagName && el.tagName.toLowerCase() !== "select")) return;
    if (lastOpenedSelectRef.current === el) return;
    lastOpenedSelectRef.current = el;
    setTimeout(() => {
      if (lastOpenedSelectRef.current === el) {
        lastOpenedSelectRef.current = null;
      }
    }, 400);
    try {
      if (typeof el.showPicker === "function") {
        el.showPicker();
      }
    } catch (err) {}
  }, []);

  const navigateFormFields = (currentElement, direction, form) => {
    if (!currentElement || !form) return false;

    const focusable = Array.from(
      form.querySelectorAll(
        "input:not([type='hidden']):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])",
      ),
    ).filter((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && el.offsetParent !== null;
    });

    const currentIndex = focusable.indexOf(currentElement);
    if (currentIndex === -1) return false;

    const curRect = currentElement.getBoundingClientRect();
    const curCenterX = curRect.left + curRect.width / 2;
    const curCenterY = curRect.top + curRect.height / 2;

    let bestElement = null;

    if (direction === "right") {
      // 1. Look for elements in the same visual row to the right
      const rowRight = focusable.filter((el) => {
        if (el === currentElement) return false;
        const r = el.getBoundingClientRect();
        const centerY = r.top + r.height / 2;
        return (
          Math.abs(centerY - curCenterY) <=
            Math.max(curRect.height, r.height) * 0.7 &&
          r.left >= curRect.left + 5
        );
      });

      if (rowRight.length > 0) {
        rowRight.sort(
          (a, b) =>
            a.getBoundingClientRect().left - b.getBoundingClientRect().left,
        );
        bestElement = rowRight[0];
      } else {
        // 2. Wrap to the first element in the next row below
        const nextRows = focusable.filter((el) => {
          if (el === currentElement) return false;
          const r = el.getBoundingClientRect();
          return r.top >= curRect.bottom - 5;
        });
        if (nextRows.length > 0) {
          nextRows.sort((a, b) => {
            const rA = a.getBoundingClientRect();
            const rB = b.getBoundingClientRect();
            if (Math.abs(rA.top - rB.top) > 10) return rA.top - rB.top;
            return rA.left - rB.left;
          });
          bestElement = nextRows[0];
        }
      }
    } else if (direction === "left") {
      // 1. Look for elements in the same visual row to the left
      const rowLeft = focusable.filter((el) => {
        if (el === currentElement) return false;
        const r = el.getBoundingClientRect();
        const centerY = r.top + r.height / 2;
        return (
          Math.abs(centerY - curCenterY) <=
            Math.max(curRect.height, r.height) * 0.7 &&
          r.right <= curRect.right - 5
        );
      });

      if (rowLeft.length > 0) {
        rowLeft.sort(
          (a, b) =>
            b.getBoundingClientRect().right - a.getBoundingClientRect().right,
        );
        bestElement = rowLeft[0];
      } else {
        // 2. Wrap to the last element in the previous row above
        const prevRows = focusable.filter((el) => {
          if (el === currentElement) return false;
          const r = el.getBoundingClientRect();
          return r.bottom <= curRect.top + 5;
        });
        if (prevRows.length > 0) {
          prevRows.sort((a, b) => {
            const rA = a.getBoundingClientRect();
            const rB = b.getBoundingClientRect();
            if (Math.abs(rA.bottom - rB.bottom) > 10)
              return rB.bottom - rA.bottom;
            return rB.right - rA.right;
          });
          bestElement = prevRows[0];
        }
      }
    } else if (direction === "down") {
      const below = focusable.filter((el) => {
        if (el === currentElement) return false;
        const r = el.getBoundingClientRect();
        const centerY = r.top + r.height / 2;
        return centerY > curCenterY + 10;
      });

      if (below.length > 0) {
        let minDy = Infinity;
        below.forEach((el) => {
          const r = el.getBoundingClientRect();
          const dy = r.top + r.height / 2 - curCenterY;
          if (dy < minDy) minDy = dy;
        });

        const nextRow = below.filter((el) => {
          const r = el.getBoundingClientRect();
          const dy = r.top + r.height / 2 - curCenterY;
          return dy <= minDy + 22;
        });

        nextRow.sort((a, b) => {
          const rA = a.getBoundingClientRect();
          const rB = b.getBoundingClientRect();
          const distA = Math.abs(rA.left + rA.width / 2 - curCenterX);
          const distB = Math.abs(rB.left + rB.width / 2 - curCenterX);
          return distA - distB;
        });
        bestElement = nextRow[0];
      }
    } else if (direction === "up") {
      const above = focusable.filter((el) => {
        if (el === currentElement) return false;
        const r = el.getBoundingClientRect();
        const centerY = r.top + r.height / 2;
        return centerY < curCenterY - 10;
      });

      if (above.length > 0) {
        let minDy = Infinity;
        above.forEach((el) => {
          const r = el.getBoundingClientRect();
          const dy = curCenterY - (r.top + r.height / 2);
          if (dy < minDy) minDy = dy;
        });

        const prevRow = above.filter((el) => {
          const r = el.getBoundingClientRect();
          const dy = curCenterY - (r.top + r.height / 2);
          return dy <= minDy + 22;
        });

        prevRow.sort((a, b) => {
          const rA = a.getBoundingClientRect();
          const rB = b.getBoundingClientRect();
          const distA = Math.abs(rA.left + rA.width / 2 - curCenterX);
          const distB = Math.abs(rB.left + rB.width / 2 - curCenterX);
          return distA - distB;
        });
        bestElement = prevRow[0];
      }
    }

    if (bestElement) {
      bestElement.focus();
      openSelectPicker(bestElement);
      if (
        typeof bestElement.select === "function" &&
        (bestElement.type === "text" ||
          bestElement.type === "search" ||
          bestElement.type === "email" ||
          bestElement.type === "url" ||
          bestElement.type === "tel")
      ) {
        try {
          bestElement.select();
        } catch (e) {}
      }
      bestElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
      return true;
    }
    return false;
  };

  const handleSupplierFormKeyDown = useCallback(
    (e) => {
      // 1. Enter key handling: moves to next field, focuses Save at end
      if (e.key === "Enter") {
        // If Enter was pressed on the Save button or any submit button, let native submission occur!
        if (
          e.target === saveSupplierBtnRef.current ||
          (e.target.tagName &&
            e.target.tagName.toLowerCase() === "button" &&
            e.target.type === "submit")
        ) {
          return;
        }

        // If Enter was pressed on other buttons (e.g. Reset, Cancel), allow default button action
        if (e.target.tagName && e.target.tagName.toLowerCase() === "button") {
          return;
        }

        // Allow Shift+Enter inside textarea for multiline text
        if (
          e.shiftKey &&
          e.target.tagName &&
          e.target.tagName.toLowerCase() === "textarea"
        ) {
          return;
        }

        // Prevent direct form submission on Enter inside input/select/textarea
        e.preventDefault();

        if (!supplierFormRef.current) return;

        // Find all focusable input, select, and textarea elements inside the form
        const formElements = Array.from(
          supplierFormRef.current.querySelectorAll(
            "input:not([type='hidden']):not([disabled]), select:not([disabled]), textarea:not([disabled])",
          ),
        );

        const currentIndex = formElements.indexOf(e.target);

        if (currentIndex !== -1) {
          if (e.shiftKey) {
            // Shift+Enter navigates to previous field
            if (currentIndex > 0) {
              const prevElement = formElements[currentIndex - 1];
              prevElement.focus();
              openSelectPicker(prevElement);
              if (
                typeof prevElement.select === "function" &&
                (prevElement.type === "text" ||
                  prevElement.type === "search" ||
                  prevElement.type === "email" ||
                  prevElement.type === "url" ||
                  prevElement.type === "tel")
              ) {
                try {
                  prevElement.select();
                } catch (err) {}
              }
              prevElement.scrollIntoView({
                block: "nearest",
                behavior: "smooth",
              });
            }
          } else {
            // Normal Enter navigates to next field, or to Save button after the last field
            if (currentIndex < formElements.length - 1) {
              const nextElement = formElements[currentIndex + 1];
              nextElement.focus();
              openSelectPicker(nextElement);
              if (
                typeof nextElement.select === "function" &&
                (nextElement.type === "text" ||
                  nextElement.type === "search" ||
                  nextElement.type === "email" ||
                  nextElement.type === "url" ||
                  nextElement.type === "tel")
              ) {
                try {
                  nextElement.select();
                } catch (err) {}
              }
              nextElement.scrollIntoView({
                block: "nearest",
                behavior: "smooth",
              });
            } else {
              // After the last input cell, focus the Save button!
              if (saveSupplierBtnRef.current) {
                saveSupplierBtnRef.current.focus();
                saveSupplierBtnRef.current.scrollIntoView({
                  block: "nearest",
                  behavior: "smooth",
                });
              }
            }
          }
        }
        return;
      }

      // 2. 4-Directional Arrow key navigation: ArrowUp, ArrowDown, ArrowLeft, ArrowRight
      const isArrowKey =
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight";

      if (!isArrowKey) return;
      if (!supplierFormRef.current) return;

      const target = e.target;
      if (!target) return;

      const tagName = target.tagName ? target.tagName.toLowerCase() : "";
      const isTextControl =
        (tagName === "input" &&
          (target.type === "text" ||
            target.type === "search" ||
            target.type === "email" ||
            target.type === "url" ||
            target.type === "tel")) ||
        tagName === "textarea";

      if (e.key === "ArrowUp") {
        if (e.altKey) return; // Allow Alt+ArrowUp for browser actions
        if (tagName === "textarea") {
          const val = target.value || "";
          const pos = target.selectionStart || 0;
          const textBefore = val.slice(0, pos);
          if (textBefore.includes("\n")) return; // Let cursor move up lines inside textarea
        }
        e.preventDefault();
        navigateFormFields(target, "up", supplierFormRef.current);
      } else if (e.key === "ArrowDown") {
        if (e.altKey) return; // Allow Alt+ArrowDown to open native select dropdown
        if (tagName === "textarea") {
          const val = target.value || "";
          const pos = target.selectionEnd || 0;
          const textAfter = val.slice(pos);
          if (textAfter.includes("\n")) return; // Let cursor move down lines inside textarea
        }
        e.preventDefault();
        navigateFormFields(target, "down", supplierFormRef.current);
      } else if (e.key === "ArrowLeft") {
        if (isTextControl) {
          const val = target.value || "";
          const selStart = target.selectionStart;
          const selEnd = target.selectionEnd;
          const isAllSelected = selStart === 0 && selEnd === val.length;
          const isAtStart = selStart === 0 && selEnd === 0;
          if (!isAllSelected && !isAtStart) return; // Let caret move inside text
        }
        e.preventDefault();
        navigateFormFields(target, "left", supplierFormRef.current);
      } else if (e.key === "ArrowRight") {
        if (isTextControl) {
          const val = target.value || "";
          const selStart = target.selectionStart;
          const selEnd = target.selectionEnd;
          const isAllSelected = selStart === 0 && selEnd === val.length;
          const isAtEnd = selStart === val.length;
          if (!isAllSelected && !isAtEnd) return; // Let caret move inside text
        }
        e.preventDefault();
        navigateFormFields(target, "right", supplierFormRef.current);
      }
    },
    [openSelectPicker],
  );

  useEffect(() => {
    if (showAddSupplierView) {
      const timer = setTimeout(() => {
        const firstInput =
          supplierFormRef.current?.querySelector('input[name="name"]');
        if (firstInput) {
          firstInput.focus();
          try {
            firstInput.select();
          } catch (e) {}
        }
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [showAddSupplierView]);

  const handleSupplierFormFocus = useCallback(
    (e) => {
      if (
        e.target &&
        (e.target.tagName === "INPUT" ||
          e.target.tagName === "SELECT" ||
          e.target.tagName === "TEXTAREA")
      ) {
        e.target.style.backgroundColor = "#FEF08A";
        e.target.style.borderColor = "#EAB308";
        e.target.style.boxShadow = "0 0 0 2px rgba(234, 179, 8, 0.25)";
      }

      if (e.target && e.target.tagName === "SELECT") {
        openSelectPicker(e.target);
      }
    },
    [openSelectPicker],
  );

  const handleSupplierFormBlur = useCallback((e) => {
    if (e.target === lastOpenedSelectRef.current) {
      lastOpenedSelectRef.current = null;
    }
    if (
      e.target &&
      (e.target.tagName === "INPUT" ||
        e.target.tagName === "SELECT" ||
        e.target.tagName === "TEXTAREA")
    ) {
      e.target.style.backgroundColor = "#FFFFFF";
      e.target.style.borderColor = "#E2E8F0";
      e.target.style.boxShadow = "none";
    }
  }, []);

  const handleSearchKeyDown = (e) => {
    // Ctrl+N shortcut to switch to Add Supplier view
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "n") {
      e.preventDefault();
      e.stopPropagation();
      setShowAddSupplierView(true);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedSupplierIndex((prev) =>
        prev < filteredSuppliers.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedSupplierIndex((prev) =>
        prev > 0 ? prev - 1 : filteredSuppliers.length - 1,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (
        filteredSuppliers.length > 0 &&
        filteredSuppliers[highlightedSupplierIndex]
      ) {
        handleSelectSupplier(filteredSuppliers[highlightedSupplierIndex]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      if (showAddSupplierView) {
        setShowAddSupplierView(false);
      } else {
        handleCloseSupplierModal();
      }
    }
  };

  useEffect(() => {
    if (supplierModalOpen) {
      setSupplierSearch("");
      const initialIndex = suppliers.findIndex(
        (s) =>
          (s._id || s.id) === (selectedSupplier?._id || selectedSupplier?.id),
      );
      setHighlightedSupplierIndex(initialIndex >= 0 ? initialIndex : 0);
      const timer = setTimeout(() => {
        if (supplierSearchInputRef.current) {
          supplierSearchInputRef.current.focus();
          supplierSearchInputRef.current.select?.();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [
    supplierModalOpen,
    suppliers,
    selectedSupplier?._id,
    selectedSupplier?.id,
  ]);

  useEffect(() => {
    setHighlightedSupplierIndex(0);
  }, [supplierSearch]);

  useEffect(() => {
    if (supplierModalOpen && supplierListContainerRef.current) {
      const activeEl = supplierListContainerRef.current.querySelector(
        `[data-supplier-index="${highlightedSupplierIndex}"]`,
      );
      if (activeEl) {
        activeEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [highlightedSupplierIndex, supplierModalOpen]);

  // Initial focus on Supplier / Party Name and focus on its search text on page load
  useEffect(() => {
    setSupplierModalOpen(true);
    const timer = setTimeout(() => {
      if (supplierSearchInputRef.current) {
        supplierSearchInputRef.current.focus();
        supplierSearchInputRef.current.select?.();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Product Modal Handlers
  const handleOpenProductModal = (rowIndex) => {
    if (justClosedProductModalRef.current) return;
    setActiveProductRowIndex(rowIndex);
    setSelectedRowIndex(rowIndex);
    setShowAddProductView(false);
    setProductModalOpen(true);
  };

  const handleCloseProductModal = useCallback(() => {
    setShowAddProductView(false);
    justClosedProductModalRef.current = true;
    isArrowNavigatingRef.current = true;
    setProductModalOpen(false);

    const pendingAppended = appendedRowViaEnterRef.current;

    if (pendingAppended) {
      appendedRowViaEnterRef.current = null;
      const { id: rowToDeleteId, aboveRowIndex } = pendingAppended;

      setItems((prev) => {
        return prev.filter((item) => item.id !== rowToDeleteId);
      });

      setSelectedRowIndex(aboveRowIndex);

      setTimeout(() => {
        const aboveEl = document.getElementById(`row-${aboveRowIndex}-product`);
        if (aboveEl) {
          aboveEl.focus({ preventScroll: true });
          ensureElementVisible(aboveEl);
        }
        setTimeout(() => {
          justClosedProductModalRef.current = false;
          isArrowNavigatingRef.current = false;
        }, 150);
      }, 60);
      return;
    }

    const targetRow = activeProductRowIndex;
    setTimeout(() => {
      if (targetRow !== null) {
        const targetEl = document.getElementById(`row-${targetRow}-product`);
        targetEl?.focus({ preventScroll: true });
        if (targetEl) ensureElementVisible(targetEl);
      }
      setTimeout(() => {
        justClosedProductModalRef.current = false;
        isArrowNavigatingRef.current = false;
      }, 150);
    }, 60);
  }, [activeProductRowIndex, ensureElementVisible]);

  const handleSelectProduct = useCallback(
    (product) => {
      justClosedProductModalRef.current = true;
      appendedRowViaEnterRef.current = null;

      const targetRow = activeProductRowIndex;

      if (targetRow !== null && product) {
        updateItemField(targetRow, "product", product);
      }

      setProductModalOpen(false);
      setProductSearchText("");
      setHighlightedProductIndex(0);

      setTimeout(() => {
        justClosedProductModalRef.current = false;

        if (targetRow !== null) {
          const next = document.getElementById(`row-${targetRow}-batch`);

          if (next) {
            next.focus();
            next.select?.();
          }
        }
      }, 100);
    },
    [activeProductRowIndex, updateItemField],
  );

  const handleCreateProduct = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const trimmedName = newProductData.name?.trim();
    const trimmedUnit1 = newProductData.unit1?.trim();

    if (!trimmedName) {
      setSnackbar({
        open: true,
        message: "Please enter product name",
        severity: "error",
      });
      return;
    }

    if (!trimmedUnit1) {
      setSnackbar({
        open: true,
        message: "Please enter Unit 1",
        severity: "error",
      });
      return;
    }

    setSavingNewProduct(true);
    try {
      const payload = {
        name: trimmedName,
        unit1: trimmedUnit1,
        unit2: newProductData.unit2?.trim() || null,
        conversionQty:
          newProductData.unit2?.trim() && newProductData.conversionQty
            ? Number(newProductData.conversionQty)
            : 1,
        primaryUnit: newProductData.primaryUnit || "unit1",
        tax: Number(newProductData.tax || 0),
        allowDecimal:
          newProductData.allowDecimal === "Yes" ||
          newProductData.allowDecimal === true,
      };

      const token =
        localStorage.getItem("accessToken") || localStorage.getItem("token");

      const response = await fetch(`${BASE_URL}api/user/products`, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        setSnackbar({
          open: true,
          message: data.message || "Failed to create product",
          severity: "error",
        });
        return;
      }

      const createdProduct = data.data;

      // Update local product results list so it's readily available
      setProductResults((prev) => [createdProduct, ...prev]);

      // Reset form and view
      setShowAddProductView(false);
      handleResetNewProduct();

      setSnackbar({
        open: true,
        message: data.message || "Product created successfully",
        severity: "success",
      });

      // Auto-select newly created product and populate receipt row
      handleSelectProduct(createdProduct);
    } catch (error) {
      console.error("Create product error:", error);
      setSnackbar({
        open: true,
        message: error.message || "Unable to save product. Please try again.",
        severity: "error",
      });
    } finally {
      setSavingNewProduct(false);
    }
  };

  const handleProductSearchKeyDown = (e) => {
    // Ctrl+N shortcut to switch to Add Product view
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "n") {
      e.preventDefault();
      e.stopPropagation();
      setShowAddProductView(true);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedProductIndex((prev) =>
        prev < productResults.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedProductIndex((prev) =>
        prev > 0 ? prev - 1 : Math.max(0, productResults.length - 1),
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (
        productResults.length > 0 &&
        productResults[highlightedProductIndex]
      ) {
        handleSelectProduct(productResults[highlightedProductIndex]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCloseProductModal();
    }
  };

  useEffect(() => {
    if (productModalOpen) {
      setProductSearchText("");
      getProducts("", 1, 20);
      setHighlightedProductIndex(0);
      setTimeout(() => {
        productSearchInputRef.current?.focus();
      }, 50);
    }
  }, [productModalOpen, getProducts]);

  useEffect(() => {
    if (!productModalOpen) return;

    const timer = setTimeout(() => {
      getProducts(productSearchText.trim(), 1, 20);
    }, 300);

    return () => clearTimeout(timer);
  }, [productSearchText, productModalOpen, getProducts]);

  useEffect(() => {
    if (productResults.length > 0) {
      const currentRowProduct =
        activeProductRowIndex !== null
          ? items[activeProductRowIndex]?.product
          : null;
      const currentId = currentRowProduct?._id || currentRowProduct?.id;
      const initialIndex = productResults.findIndex(
        (p) => (p._id || p.id) === currentId,
      );
      setHighlightedProductIndex(initialIndex >= 0 ? initialIndex : 0);
    } else {
      setHighlightedProductIndex(0);
    }
  }, [productResults, activeProductRowIndex, items]);

  useEffect(() => {
    if (productModalOpen && productListContainerRef.current) {
      const activeEl = productListContainerRef.current.querySelector(
        `[data-product-index="${highlightedProductIndex}"]`,
      );
      if (activeEl) {
        activeEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [highlightedProductIndex, productModalOpen]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (supplierModalOpen || productModalOpen) return;

      // Ctrl+N shortcut to open Add Product directly
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "n") {
        e.preventDefault();
        const targetRow =
          selectedRowIndex !== null && selectedRowIndex >= 0
            ? selectedRowIndex
            : 0;
        handleOpenProductModal(targetRow);
        setShowAddProductView(true);
        return;
      }

      // F2 or Alt+N to add new row
      if (e.key === "F2" || (e.altKey && e.key.toLowerCase() === "n")) {
        e.preventDefault();
        addNewRow();
      }
      // F4 or Alt+S to save bill
      else if (e.key === "F4" || (e.altKey && e.key.toLowerCase() === "s")) {
        e.preventDefault();
        handleSaveBill();
      }
      // F1 for help
      else if (e.key === "F1") {
        e.preventDefault();
        setShowShortcutsModal((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [
    addNewRow,
    handleSaveBill,
    supplierModalOpen,
    productModalOpen,
    selectedRowIndex,
  ]);

  useEffect(() => {
    const handleProductModalKeyDown = (e) => {
      if (!productModalOpen) return;

      // Ctrl+N shortcut to switch to Add Product view
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "n") {
        e.preventDefault();
        e.stopPropagation();
        setShowAddProductView(true);
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (showAddProductView) {
          setShowAddProductView(false);
        } else {
          handleCloseProductModal();
        }
      }
    };
    if (productModalOpen) {
      window.addEventListener("keydown", handleProductModalKeyDown, true);
    }
    return () => {
      window.removeEventListener("keydown", handleProductModalKeyDown, true);
    };
  }, [productModalOpen, showAddProductView, handleCloseProductModal]);

  useEffect(() => {
    const handleSupplierModalKeyDown = (e) => {
      if (!supplierModalOpen) return;

      // Ctrl+N shortcut to switch to Add Supplier view
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "n") {
        e.preventDefault();
        e.stopPropagation();
        setShowAddSupplierView(true);
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (showAddSupplierView) {
          setShowAddSupplierView(false);
        } else {
          handleCloseSupplierModal();
        }
      }
    };
    if (supplierModalOpen) {
      window.addEventListener("keydown", handleSupplierModalKeyDown, true);
    }
    return () => {
      window.removeEventListener("keydown", handleSupplierModalKeyDown, true);
    };
  }, [supplierModalOpen, showAddSupplierView, handleCloseSupplierModal]);

  return (
    <div
      style={{
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        minHeight: "calc(100vh - 110px)",
      }}
    >
      {/* =========================================================
          SUPPLIER & RECEIPT INFORMATION CARD
      ========================================================== */}
      <div
        style={{
          ...cardStyle,
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {/* Section Header */}
        <div
          style={{
            minHeight: "48px",
            padding: "0 18px",
            borderBottom: "1px solid #EDE9FE",
            background: THEME.lighter,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "7px",
                background: THEME.iconBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <StorefrontOutlined
                style={{ fontSize: "18px", color: THEME.primary }}
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: 700,
                  color: THEME.text,
                }}
              >
                Receipt Details
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <button
              type="button"
              onClick={() => setShowShortcutsModal(true)}
              style={{
                height: "34px",
                padding: "0 13px",
                borderRadius: "6px",
                border: "1px solid #E2E8F0",
                background: "#FFFFFF",
                color: THEME.muted,
                fontSize: "13px",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#F8FAFC";
                e.currentTarget.style.borderColor = "#CBD5E1";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#FFFFFF";
                e.currentTarget.style.borderColor = "#E2E8F0";
              }}
            >
              <KeyboardIcon style={{ fontSize: "16px" }} />
              Shortcuts (F1)
            </button>
          </div>
        </div>

        {/* Card Body */}
        <div style={{ padding: "10px 14px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "minmax(260px, 2.5fr) minmax(140px, 1fr) minmax(140px, 1fr) minmax(140px, 1fr)",
              gap: "10px",
            }}
            className="receipt-header-grid"
          >
            {/* Supplier / Party Name (Focus/Click triggers search popup) */}
            <div>
              <label style={labelStyle}>
                SUPPLIER / PARTY NAME{" "}
                <span style={{ color: THEME.danger }}>*</span>
              </label>
              <div style={{ position: "relative" }}>
                <input
                  id="supplier-input"
                  readOnly
                  style={{
                    ...inputBaseStyle,
                    cursor: "pointer",
                    paddingRight: "36px",
                    backgroundColor: "#FFFFFF",
                    fontWeight: selectedSupplier ? 600 : 400,
                  }}
                  value={selectedSupplier ? selectedSupplier.name : ""}
                  placeholder="Click or focus to search supplier..."
                  tabIndex={1}
                  onFocus={handleOpenSupplierModal}
                  onClick={handleOpenSupplierModal}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleOpenSupplierModal();
                    }
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <SearchOutlined
                    style={{ fontSize: "18px", color: THEME.primary }}
                  />
                </div>
              </div>
            </div>

            {/* Receipt No */}
            <div>
              <label style={labelStyle}>
                RECEIPT / BILL NO.{" "}
                <span style={{ color: THEME.danger }}>*</span>
              </label>
              <input
                id="receipt-no-input"
                style={inputBaseStyle}
                placeholder="e.g. REC-2024-001"
                value={receiptNo}
                onChange={(e) => setReceiptNo(e.target.value)}
                tabIndex={2}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const nextEl =
                      document.getElementById("receipt-date-input");
                    nextEl?.focus();
                    nextEl?.select?.();
                  }
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = THEME.accent;
                  e.target.style.boxShadow = "0 0 0 3px rgba(109,40,217,0.12)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E2E8F0";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            {/* Receipt Date */}
            <div>
              <label style={labelStyle}>
                RECEIPT DATE <span style={{ color: THEME.danger }}>*</span>
              </label>
              <input
                id="receipt-date-input"
                type="date"
                style={inputBaseStyle}
                value={receiptDate}
                onChange={(e) => setReceiptDate(e.target.value)}
                tabIndex={3}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const nextEl = document.getElementById(
                      "item-received-date-input",
                    );
                    nextEl?.focus();
                    nextEl?.select?.();
                  }
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = THEME.accent;
                  e.target.style.boxShadow = "0 0 0 3px rgba(109,40,217,0.12)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E2E8F0";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            {/* Item Received Date */}
            <div>
              <label style={labelStyle}>
                ITEM RECEIVED DATE{" "}
                <span style={{ color: THEME.danger }}>*</span>
              </label>
              <input
                id="item-received-date-input"
                type="date"
                style={inputBaseStyle}
                value={itemReceivedDate}
                onChange={(e) => setItemReceivedDate(e.target.value)}
                tabIndex={4}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (items.length === 0) {
                      addNewRow();
                    } else {
                      setSelectedRowIndex(0);
                      const firstCell =
                        document.getElementById("row-0-product");
                      firstCell?.focus();
                      firstCell?.select?.();
                    }
                  }
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = THEME.accent;
                  e.target.style.boxShadow = "0 0 0 3px rgba(109,40,217,0.12)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E2E8F0";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          {/* Quick Supplier Info Strip (Station & Address) */}
          {selectedSupplier &&
            (selectedSupplier.station || selectedSupplier.address) && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "8px 14px",
                  background: "#F8FAFC",
                  borderRadius: "6px",
                  border: "1px solid #E2E8F0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "12px",
                  fontSize: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  {/* Station */}
                  {selectedSupplier.station && (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span style={{ color: THEME.muted, fontWeight: 600 }}>
                        Station:
                      </span>
                      <span
                        style={{
                          color: "#1E293B",
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        {selectedSupplier.station}
                      </span>
                    </div>
                  )}

                  {/* Address */}
                  {selectedSupplier.address && (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span style={{ color: THEME.muted, fontWeight: 600 }}>
                        Address:
                      </span>
                      <span style={{ color: "#334155", fontWeight: 500 }}>
                        {selectedSupplier.address}
                      </span>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setSelectedSupplier(null);
                    setTimeout(
                      () => document.getElementById("supplier-input")?.focus(),
                      50,
                    );
                  }}
                  style={{
                    background: "transparent",
                    border: "1px solid #CBD5E1",
                    borderRadius: "4px",
                    color: "#64748B",
                    cursor: "pointer",
                    fontSize: "11px",
                    fontWeight: 600,
                    padding: "2px 8px",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#DC2626";
                    e.currentTarget.style.borderColor = "#F87171";
                    e.currentTarget.style.background = "#FEF2F2";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#64748B";
                    e.currentTarget.style.borderColor = "#CBD5E1";
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  ✕ Change
                </button>
              </div>
            )}
        </div>
      </div>

      {/* =========================================================
          LINE ITEMS GRID CARD
      ========================================================== */}
      <div
        style={{
          ...cardStyle,
          overflow: "hidden",
        }}
      >
        {/* Table Header Toolbar */}
        <div
          style={{
            minHeight: "48px",
            padding: "0 18px",
            borderBottom: "1px solid #E2E8F0",
            background: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "7px",
                background: THEME.iconBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Inventory2Outlined
                style={{ fontSize: "18px", color: THEME.primary }}
              />
            </div>
            <div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color: THEME.text,
                  }}
                >
                  Items Grid
                </span>
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: "10px",
                    background: THEME.light,
                    color: THEME.primary,
                    fontSize: "12px",
                    fontWeight: 700,
                  }}
                >
                  {calculatedItems.length}{" "}
                  {calculatedItems.length === 1 ? "Item" : "Items"}
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={addNewRow}
            style={{
              height: "34px",
              padding: "0 13px",
              borderRadius: "6px",
              border: `1px solid ${THEME.border}`,
              background: THEME.light,
              color: THEME.primary,
              fontSize: "13px",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = THEME.iconBg)
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = THEME.light)
            }
          >
            <AddIcon style={{ fontSize: "16px" }} />
            Add Item (F2)
          </button>
        </div>

        {/* Table Container - Fixed Height for 6 rows with Overflow Scroll */}
        <div
          className="receipt-table-scroll"
          style={{
            height: "270px",
            overflow: "auto",
          }}
        >
          <table
            className="receipt-grid-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13.5px",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    ...tableHeadStyle,
                    width: "36px",
                    textAlign: "center",
                  }}
                >
                  #
                </th>
                <th style={{ ...tableHeadStyle, minWidth: "220px" }}>
                  PRODUCT NAME <span style={{ color: THEME.danger }}>*</span>
                </th>
                <th
                  style={{
                    ...tableHeadStyle,
                    width: "70px",
                    textAlign: "center",
                  }}
                >
                  BATCH NO. <span style={{ color: THEME.danger }}>*</span>
                </th>
                <th
                  style={{
                    ...tableHeadStyle,
                    width: "70px",
                    textAlign: "center",
                  }}
                >
                  QTY <span style={{ color: THEME.danger }}>*</span>
                </th>
                <th
                  style={{
                    ...tableHeadStyle,
                    width: "80px",
                    textAlign: "center",
                  }}
                >
                  UNIT <span style={{ color: THEME.danger }}>*</span>
                </th>
                <th
                  style={{
                    ...tableHeadStyle,
                    width: "85px",
                    textAlign: "right",
                  }}
                >
                  RATE (₹) <span style={{ color: THEME.danger }}>*</span>
                </th>

                <th
                  style={{
                    ...tableHeadStyle,
                    width: "105px",
                    textAlign: "center",
                  }}
                >
                  DISC 1
                </th>
                <th
                  style={{
                    ...tableHeadStyle,
                    width: "105px",
                    textAlign: "center",
                  }}
                >
                  DISC 2
                </th>
                <th
                  style={{
                    ...tableHeadStyle,
                    textAlign: "right",
                    width: "95px",
                  }}
                >
                  BEFORE TAX
                </th>
                <th
                  style={{
                    ...tableHeadStyle,
                    width: "70px",
                    textAlign: "center",
                  }}
                >
                  TAX %
                </th>
                <th
                  style={{
                    ...tableHeadStyle,
                    textAlign: "right",
                    width: "100px",
                  }}
                >
                  NET AMT
                </th>
                <th
                  style={{
                    ...tableHeadStyle,
                    textAlign: "center",
                    width: "45px",
                  }}
                >
                  ACT
                </th>
              </tr>
            </thead>
            <tbody>
              {calculatedItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={12}
                    style={{
                      padding: "48px 16px",
                      textAlign: "center",
                      color: THEME.muted,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Inventory2Outlined
                        style={{ fontSize: "38px", color: THEME.border }}
                      />
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: THEME.lightText,
                        }}
                      >
                        No line items added yet
                      </div>
                      <div style={{ fontSize: "12px", color: THEME.muted }}>
                        Complete supplier details above and press Enter, or
                        click Add Item below to start
                      </div>
                      <button
                        type="button"
                        onClick={addNewRow}
                        style={{
                          marginTop: "6px",
                          height: "34px",
                          padding: "0 15px",
                          borderRadius: "6px",
                          border: `1px solid ${THEME.border}`,
                          background: THEME.light,
                          color: THEME.primary,
                          fontSize: "13px",
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = THEME.iconBg;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = THEME.light;
                        }}
                      >
                        <AddIcon style={{ fontSize: "16px" }} />
                        Add First Item (F2)
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                calculatedItems.map((row, index) => {
                  const isSelected = selectedRowIndex === index;
                  return (
                    <tr
                      id={`receipt-row-${index}`}
                      key={row.id}
                      tabIndex={0}
                      className={
                        isSelected && isRowFocused ? "receipt-row-focused" : ""
                      }
                      onClick={(e) => {
                        setSelectedRowIndex(index);
                        if (
                          e.target === e.currentTarget ||
                          e.target.tagName === "TD"
                        ) {
                          focusRow(index);
                        }
                      }}
                      onFocus={(e) => {
                        if (e.target === e.currentTarget) {
                          setSelectedRowIndex(index);
                          setIsRowFocused(true);
                          ensureElementVisible(e.currentTarget);
                        }
                      }}
                      onKeyDown={(e) => handleRowKeyDown(e, index)}
                      style={{
                        background: isSelected
                          ? isRowFocused
                            ? "#EDE9FE"
                            : "#F5F3FF"
                          : "#FFFFFF",
                        outline:
                          isSelected && isRowFocused
                            ? `2px solid ${THEME.primary}`
                            : "none",
                        outlineOffset: "-2px",
                        cursor: "default",
                        transition: "background-color 0.12s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected)
                          e.currentTarget.style.background = "#F8FAFC";
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected)
                          e.currentTarget.style.background = "#FFFFFF";
                      }}
                    >
                      {/* Index */}
                      <td
                        style={{
                          padding: 0,
                          height: "38px",
                          textAlign: "center",
                          fontWeight: 700,
                          fontSize: "13.5px",
                          color: isSelected ? THEME.primaryDark : THEME.muted,
                          border: "1px solid #CBD5E1",
                          borderLeft: isSelected
                            ? `5px solid ${THEME.primary}`
                            : "1px solid #CBD5E1",
                          background: isSelected
                            ? isRowFocused
                              ? "#DDD6FE"
                              : "#EDE9FE"
                            : undefined,
                          userSelect: "none",
                          verticalAlign: "middle",
                        }}
                      >
                        {index + 1}
                      </td>

                      {/* Product Name Custom Popup Trigger */}
                      <td
                        style={{
                          padding: 0,
                          height: "38px",
                          border: "1px solid #CBD5E1",
                        }}
                      >
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <input
                            id={`row-${index}-product`}
                            readOnly
                            className="receipt-grid-cell-input"
                            value={row.product ? row.product.name : ""}
                            placeholder="Click / Focus to select product..."
                            onFocus={() => {
                              if (!isArrowNavigatingRef.current) {
                                handleOpenProductModal(index);
                              }
                            }}
                            onClick={() => handleOpenProductModal(index)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleOpenProductModal(index);
                              } else {
                                handleGridArrowNav(e, index, "product");
                              }
                            }}
                            style={{
                              paddingLeft: "8px",
                              paddingRight: "28px",
                              fontSize: "15.5px",
                              fontWeight: 600,
                              color: row.product ? THEME.text : THEME.muted,
                              cursor: "pointer",
                            }}
                          />
                          <SearchOutlined
                            style={{
                              position: "absolute",
                              right: "8px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: "16px",
                              color: THEME.primary,
                              pointerEvents: "none",
                            }}
                          />
                        </div>
                      </td>

                      {/* Batch No. */}
                      <td
                        style={{
                          padding: 0,
                          height: "38px",
                          border: "1px solid #CBD5E1",
                        }}
                      >
                        <input
                          id={`row-${index}-batch`}
                          type="text"
                          className="receipt-grid-cell-input"
                          style={{
                            padding: "0 8px",
                            textAlign: "center",
                            fontSize: "15.5px",
                            fontWeight: 800,
                          }}
                          value={row.batchNo ?? row.batch ?? ""}
                          onChange={(e) =>
                            updateItemField(index, "batchNo", e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              focusCell(index, "qty");
                            } else {
                              handleGridArrowNav(e, index, "batch");
                            }
                          }}
                        />
                      </td>

                      {/* Qty */}
                      <td
                        style={{
                          padding: 0,
                          height: "38px",
                          border: "1px solid #CBD5E1",
                        }}
                      >
                        <input
                          id={`row-${index}-qty`}
                          type="number"
                          className="receipt-grid-cell-input"
                          style={{
                            padding: "0 8px",
                            textAlign: "center",
                            fontSize: "15.5px",
                            fontWeight: 800,
                          }}
                          value={row.qty}
                          onChange={(e) =>
                            updateItemField(index, "qty", e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              focusCell(index, "unit");
                            } else {
                              handleGridArrowNav(e, index, "qty");
                            }
                          }}
                        />
                      </td>

                      {/* Unit */}
                      <td
                        style={{
                          padding: 0,
                          height: "38px",
                          border: "1px solid #CBD5E1",
                          fontWeight: 800,
                          fontSize: "15.5px",
                        }}
                      >
                        <UnitRoller
                          id={`row-${index}-unit`}
                          value={row.unit}
                          unit1={row.product?.unit1 || row.product?.unit || ""}
                          unit2={row.product?.unit2 || ""}
                          onChange={(newUnit) =>
                            updateItemField(index, "unit", newUnit)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              focusCell(index, "rate");
                            } else {
                              handleGridArrowNav(e, index, "unit");
                            }
                          }}
                        />
                      </td>

                      {/* Rate */}
                      <td
                        style={{
                          padding: 0,
                          height: "38px",
                          border: "1px solid #CBD5E1",
                        }}
                      >
                        <input
                          id={`row-${index}-rate`}
                          type="number"
                          className="receipt-grid-cell-input"
                          style={{
                            padding: "0 8px",
                            textAlign: "right",
                            fontSize: "15.5px",
                            fontWeight: 800,
                          }}
                          value={row.rate}
                          onChange={(e) =>
                            updateItemField(index, "rate", e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              focusCell(index, "disc1");
                            } else {
                              handleGridArrowNav(e, index, "rate");
                            }
                          }}
                        />
                      </td>

                      {/* Disc 1 */}
                      <td
                        style={{
                          padding: 0,
                          height: "38px",
                          border: "1px solid #CBD5E1",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "stretch",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <input
                            id={`row-${index}-disc1`}
                            type="number"
                            className="receipt-grid-cell-input"
                            style={{
                              flex: 1,
                              minWidth: 0,
                              padding: "0 6px",
                              textAlign: "right",
                              fontSize: "15.5px",
                              fontWeight: 800,
                            }}
                            value={row.disc1}
                            onChange={(e) =>
                              updateItemField(index, "disc1", e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                focusCell(index, "disc1-type");
                              } else {
                                handleGridArrowNav(e, index, "disc1");
                              }
                            }}
                          />
                          <DiscTypeRoller
                            id={`row-${index}-disc1-type`}
                            borderless
                            height="100%"
                            width="36px"
                            value={row.disc1Type}
                            onChange={(val) =>
                              updateItemField(index, "disc1Type", val)
                            }
                            onKeyDown={(e) => {
                              if (e.shiftKey && e.key === "Backspace") {
                                e.preventDefault();
                                removeRow(index, "disc1-type");
                                return;
                              }
                              if (e.key === "ArrowLeft") {
                                e.preventDefault();
                                focusCell(index, "disc1");
                              } else if (e.key === "ArrowRight") {
                                e.preventDefault();
                                focusCell(index, "disc2");
                              } else if (e.key === "ArrowUp") {
                                e.preventDefault();
                                updateItemField(index, "disc1Type", "%");
                              } else if (e.key === "ArrowDown") {
                                e.preventDefault();
                                updateItemField(index, "disc1Type", "Rs");
                              } else if (e.key === " ") {
                                e.preventDefault();
                                updateItemField(
                                  index,
                                  "disc1Type",
                                  row.disc1Type === "%" ? "Rs" : "%",
                                );
                              } else if (
                                e.key.toLowerCase() === "p" ||
                                e.key === "%"
                              ) {
                                e.preventDefault();
                                updateItemField(index, "disc1Type", "%");
                              } else if (e.key.toLowerCase() === "r") {
                                e.preventDefault();
                                updateItemField(index, "disc1Type", "Rs");
                              } else if (e.key === "Enter") {
                                e.preventDefault();
                                focusCell(index, "disc2");
                              }
                            }}
                          />
                        </div>
                      </td>

                      {/* Disc 2 */}
                      <td
                        style={{
                          padding: 0,
                          height: "38px",
                          border: "1px solid #CBD5E1",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "stretch",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <input
                            id={`row-${index}-disc2`}
                            type="number"
                            className="receipt-grid-cell-input"
                            style={{
                              flex: 1,
                              minWidth: 0,
                              padding: "0 6px",
                              textAlign: "right",
                              fontSize: "15.5px",
                              fontWeight: 800,
                            }}
                            value={row.disc2}
                            onChange={(e) =>
                              updateItemField(index, "disc2", e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                focusCell(index, "disc2-type");
                              } else {
                                handleGridArrowNav(e, index, "disc2");
                              }
                            }}
                          />
                          <DiscTypeRoller
                            id={`row-${index}-disc2-type`}
                            borderless
                            height="100%"
                            width="36px"
                            value={row.disc2Type}
                            onChange={(val) =>
                              updateItemField(index, "disc2Type", val)
                            }
                            onKeyDown={(e) => {
                              if (e.shiftKey && e.key === "Backspace") {
                                e.preventDefault();
                                removeRow(index, "disc2-type");
                                return;
                              }
                              if (e.key === "ArrowLeft") {
                                e.preventDefault();
                                focusCell(index, "disc2");
                              } else if (e.key === "ArrowRight") {
                                e.preventDefault();
                                focusCell(index, "tax");
                              } else if (e.key === "ArrowUp") {
                                e.preventDefault();
                                updateItemField(index, "disc2Type", "%");
                              } else if (e.key === "ArrowDown") {
                                e.preventDefault();
                                updateItemField(index, "disc2Type", "Rs");
                              } else if (e.key === " ") {
                                e.preventDefault();
                                updateItemField(
                                  index,
                                  "disc2Type",
                                  row.disc2Type === "Rs" ? "%" : "Rs",
                                );
                              } else if (
                                e.key.toLowerCase() === "p" ||
                                e.key === "%"
                              ) {
                                e.preventDefault();
                                updateItemField(index, "disc2Type", "%");
                              } else if (e.key.toLowerCase() === "r") {
                                e.preventDefault();
                                updateItemField(index, "disc2Type", "Rs");
                              } else if (e.key === "Enter") {
                                e.preventDefault();
                                focusCell(index, "tax");
                              }
                            }}
                          />
                        </div>
                      </td>

                      {/* Before Tax */}
                      <td
                        style={{
                          padding: "0 10px",
                          height: "38px",
                          textAlign: "right",
                          fontWeight: 600,
                          fontSize: "15.5px",
                          color: THEME.bodyText,
                          whiteSpace: "nowrap",
                          border: "1px solid #CBD5E1",
                          verticalAlign: "middle",
                        }}
                      >
                        ₹{row.amountBeforeTax.toFixed(2)}
                      </td>

                      {/* Tax % */}
                      <td
                        style={{
                          padding: 0,
                          height: "38px",
                          border: "1px solid #CBD5E1",
                        }}
                      >
                        <input
                          id={`row-${index}-tax`}
                          type="number"
                          className="receipt-grid-cell-input"
                          style={{
                            padding: "0 8px",
                            textAlign: "center",
                            fontSize: "15.5px",
                            fontWeight: 800,
                            color: THEME.primary,
                          }}
                          value={row.tax}
                          onChange={(e) =>
                            updateItemField(index, "tax", e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleLastCellEnter(index);
                            } else {
                              handleGridArrowNav(e, index, "tax");
                            }
                          }}
                        />
                      </td>

                      {/* Net Amt */}
                      <td
                        style={{
                          padding: "0 10px",
                          height: "38px",
                          textAlign: "right",
                          fontWeight: 700,
                          fontSize: "16px",
                          color: THEME.primary,
                          whiteSpace: "nowrap",
                          border: "1px solid #CBD5E1",
                          verticalAlign: "middle",
                        }}
                      >
                        ₹{row.amountAfterTax.toFixed(2)}
                      </td>

                      {/* Action Delete */}
                      <td
                        style={{
                          padding: 0,
                          height: "38px",
                          textAlign: "center",
                          border: "1px solid #CBD5E1",
                          verticalAlign: "middle",
                        }}
                      >
                        <button
                          id={`row-${index}-delete`}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeRow(index);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              removeRow(index);
                            } else {
                              handleGridArrowNav(e, index, "delete");
                            }
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = THEME.danger;
                            e.currentTarget.style.boxShadow =
                              "0 0 0 2px rgba(239,68,68,0.35)";
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor =
                              THEME.dangerBorder;
                            e.currentTarget.style.boxShadow = "none";
                          }}
                          style={{
                            width: "30px",
                            height: "30px",
                            borderRadius: "6px",
                            border: `1px solid ${THEME.dangerBorder}`,
                            background: THEME.dangerBg,
                            color: THEME.danger,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            outline: "none",
                            transition: "all 0.15s ease",
                          }}
                          title="Remove item (or Shift + Backspace on any cell)"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#FEE2E2";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = THEME.dangerBg;
                          }}
                        >
                          <DeleteIcon style={{ fontSize: "15px" }} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* =========================================================
          HORIZONTAL BILL SUMMARY & TOTALS
      ========================================================== */}
      {/* =========================================================
          HORIZONTAL BILL SUMMARY & TOTALS (STICKY BOTTOM)
      ========================================================== */}
      <div
        style={{
          ...cardStyle,
          overflow: "hidden",
          background: "#FFFFFF",
          flexShrink: 0,
          marginTop: "auto",
          position: "sticky",
          bottom: 0,
          zIndex: 20,
          boxShadow: "0 -4px 14px rgba(15, 23, 42, 0.08)",
        }}
      >
        {/* ROW 1: Summary Calculations & Discounts Strip */}
        <div
          className="receipt-summary-row-1"
          style={{
            padding: "8px 14px",
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: "10px",
            alignItems: "center",
            background: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          {/* Sub Total = all product's sum of rate * qty */}
          <div
            style={{
              padding: "4px 10px",
              borderRadius: "6px",
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              minHeight: "40px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                fontSize: "10.5px",
                fontWeight: 700,
                color: THEME.lightText,
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                lineHeight: 1.1,
                marginBottom: "2px",
              }}
            >
              Sub Total
            </div>
            <div
              style={{
                fontSize: "14px",
                fontWeight: 700,
                color: THEME.text,
                lineHeight: 1.1,
              }}
            >
              ₹{subTotal.toFixed(2)}
            </div>
          </div>

          {/* Item Discounts */}
          <div
            style={{
              padding: "4px 10px",
              borderRadius: "6px",
              background: "#F0FDF4",
              border: "1px solid #DCFCE7",
              minHeight: "40px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                fontSize: "10.5px",
                fontWeight: 700,
                color: "#166534",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                lineHeight: 1.1,
                marginBottom: "2px",
              }}
            >
              Item Discounts
            </div>
            <div
              style={{
                fontSize: "14px",
                fontWeight: 700,
                color: THEME.success,
                lineHeight: 1.1,
              }}
            >
              -₹{totalItemDisc.toFixed(2)}
            </div>
          </div>

          {/* Grand Total */}
          <div
            style={{
              padding: "4px 10px",
              borderRadius: "6px",
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              minHeight: "40px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                fontSize: "10.5px",
                fontWeight: 700,
                color: THEME.lightText,
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                lineHeight: 1.1,
                marginBottom: "2px",
              }}
            >
              Grand Total
            </div>
            <div
              style={{
                fontSize: "14px",
                fontWeight: 700,
                color: THEME.text,
                lineHeight: 1.1,
              }}
            >
              ₹{grandTotal.toFixed(2)}
            </div>
          </div>

          {/* Bill Discount 1 */}
          <div
            style={{
              padding: "4px 10px",
              borderRadius: "6px",
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              minHeight: "40px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                lineHeight: 1.1,
                marginBottom: "2px",
              }}
            >
              <span
                style={{
                  fontSize: "10.5px",
                  fontWeight: 700,
                  color: THEME.lightText,
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                }}
              >
                Bill Disc 1
              </span>
              {schemeDiscCalculated > 0 && (
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 600,
                    color: THEME.success,
                  }}
                >
                  -₹{schemeDiscCalculated.toFixed(2)}
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <input
                type="number"
                style={{
                  ...inputBaseStyle,
                  height: "24px",
                  minHeight: "24px",
                  width: "52px",
                  fontSize: "12px",
                  padding: "0 6px",
                }}
                value={schemeDisc}
                onChange={(e) => setSchemeDisc(e.target.value)}
                onFocus={(e) => {
                  e.target.style.borderColor = THEME.accent;
                  e.target.style.boxShadow = "0 0 0 2px rgba(109,40,217,0.12)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E2E8F0";
                  e.target.style.boxShadow = "none";
                }}
              />
              <DiscTypeRoller
                id="scheme-disc-type"
                value={schemeDiscType}
                onChange={setSchemeDiscType}
                height="24px"
                width="32px"
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setSchemeDiscType("%");
                  } else if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setSchemeDiscType("Rs");
                  } else if (
                    e.key === " " ||
                    e.key === "ArrowLeft" ||
                    e.key === "ArrowRight"
                  ) {
                    e.preventDefault();
                    setSchemeDiscType((prev) => (prev === "%" ? "Rs" : "%"));
                  } else if (e.key.toLowerCase() === "p" || e.key === "%") {
                    e.preventDefault();
                    setSchemeDiscType("%");
                  } else if (e.key.toLowerCase() === "r") {
                    e.preventDefault();
                    setSchemeDiscType("Rs");
                  }
                }}
              />
            </div>
          </div>

          {/* Total GST Tax */}
          <div
            style={{
              padding: "4px 10px",
              borderRadius: "6px",
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              minHeight: "40px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                fontSize: "10.5px",
                fontWeight: 700,
                color: THEME.lightText,
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                lineHeight: 1.1,
                marginBottom: "2px",
              }}
            >
              Total GST
            </div>
            <div
              style={{
                fontSize: "14px",
                fontWeight: 700,
                color: THEME.text,
                lineHeight: 1.1,
              }}
            >
              +₹{totalTaxAmount.toFixed(2)}
            </div>
          </div>

          {/* Bill Discount 2 */}
          <div
            style={{
              padding: "4px 10px",
              borderRadius: "6px",
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              minHeight: "40px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                lineHeight: 1.1,
                marginBottom: "2px",
              }}
            >
              <span
                style={{
                  fontSize: "10.5px",
                  fontWeight: 700,
                  color: THEME.lightText,
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                }}
              >
                Bill Disc 2
              </span>
              {billDiscCalculated > 0 && (
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 600,
                    color: THEME.success,
                  }}
                >
                  -₹{billDiscCalculated.toFixed(2)}
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <input
                type="number"
                style={{
                  ...inputBaseStyle,
                  height: "24px",
                  minHeight: "24px",
                  width: "52px",
                  fontSize: "12px",
                  padding: "0 6px",
                }}
                value={billDisc}
                onChange={(e) => setBillDisc(e.target.value)}
                onFocus={(e) => {
                  e.target.style.borderColor = THEME.accent;
                  e.target.style.boxShadow = "0 0 0 2px rgba(109,40,217,0.12)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E2E8F0";
                  e.target.style.boxShadow = "none";
                }}
              />
              <DiscTypeRoller
                id="bill-disc-type"
                value={billDiscType}
                onChange={setBillDiscType}
                height="24px"
                width="32px"
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setBillDiscType("%");
                  } else if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setBillDiscType("Rs");
                  } else if (
                    e.key === " " ||
                    e.key === "ArrowLeft" ||
                    e.key === "ArrowRight"
                  ) {
                    e.preventDefault();
                    setBillDiscType((prev) => (prev === "%" ? "Rs" : "%"));
                  } else if (e.key.toLowerCase() === "p" || e.key === "%") {
                    e.preventDefault();
                    setBillDiscType("%");
                  } else if (e.key.toLowerCase() === "r") {
                    e.preventDefault();
                    setBillDiscType("Rs");
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* ROW 2: Settlement Accounts & Save Bill Strip */}
        <div
          className="receipt-summary-row-2"
          style={{
            padding: "8px 14px",
            display: "grid",
            gridTemplateColumns:
              "minmax(240px, 1.4fr) minmax(240px, 1.4fr) minmax(210px, 1.15fr) auto",
            gap: "12px",
            alignItems: "center",
            background: "#FFFFFF",
          }}
        >
          {/* Account 1 Selection & Amount */}
          <div
            style={{
              padding: "5px 12px",
              borderRadius: "8px",
              background: account1 ? "#F5F3FF" : "#F8FAFC",
              border: account1
                ? `1.5px solid ${THEME.accent}`
                : "1px solid #E2E8F0",
              minHeight: "44px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              transition: "all 0.15s ease",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                lineHeight: 1.1,
                marginBottom: "3px",
              }}
            >
              <span
                style={{
                  fontSize: "10.5px",
                  fontWeight: 700,
                  color: account1 ? THEME.primaryDark : THEME.lightText,
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                }}
              >
                Account 1
              </span>
              {account1 && amount1 !== "" && !isNaN(Number(amount1)) && (
                <span
                  style={{
                    fontSize: "10.5px",
                    fontWeight: 700,
                    color: THEME.primary,
                  }}
                >
                  ₹{Number(amount1).toFixed(2)}
                </span>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <select
                id="receipt-account-1"
                value={account1}
                onChange={(e) => {
                  const val = e.target.value;
                  setAccount1(val);
                  if (!val) {
                    setAmount1("");
                  } else {
                    setTimeout(() => {
                      amount1Ref.current?.focus();
                    }, 50);
                  }
                }}
                style={{
                  ...inputBaseStyle,
                  height: "26px",
                  minHeight: "26px",
                  fontSize: "12px",
                  padding: "0 8px",
                  flex: "1 1 60%",
                  minWidth: "90px",
                  backgroundColor: "#FFFFFF",
                  borderColor: account1 ? THEME.accent : "#CBD5E1",
                  cursor: "pointer",
                }}
              >
                <option value="">-- Choose Account --</option>
                {accountOptions.map((acc, idx) => {
                  const accName = typeof acc === "string" ? acc : acc.name;
                  const accId =
                    typeof acc === "object" && (acc._id || acc.id)
                      ? acc._id || acc.id
                      : accName;
                  const accKey =
                    (typeof acc === "object" && (acc._id || acc.id)) ||
                    `${accName}-${idx}`;
                  return (
                    <option key={accKey} value={accId}>
                      {accName}
                    </option>
                  );
                })}
              </select>

              <input
                ref={amount1Ref}
                id="receipt-amount-1"
                type="number"
                min="0"
                step="any"
                disabled={!account1}
                placeholder={account1 ? "0.00" : "Amount"}
                value={amount1}
                onChange={(e) => setAmount1(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    document.getElementById("receipt-account-2")?.focus();
                  }
                }}
                title={
                  account1
                    ? "Enter amount for selected account"
                    : "Select an account first"
                }
                style={{
                  ...inputBaseStyle,
                  height: "26px",
                  minHeight: "26px",
                  fontSize: "12.5px",
                  padding: "0 8px",
                  flex: "0 0 75px",
                  width: "75px",
                  backgroundColor: account1 ? "#FFFFFF" : "#F1F5F9",
                  cursor: account1 ? "text" : "not-allowed",
                  borderColor: account1 ? "#CBD5E1" : "#E2E8F0",
                  color: account1 ? "#1E293B" : "#94A3B8",
                }}
                onFocus={(e) => {
                  if (account1) {
                    e.target.style.borderColor = THEME.accent;
                    e.target.style.boxShadow =
                      "0 0 0 2px rgba(109,40,217,0.12)";
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#CBD5E1";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          {/* Account 2 Selection & Amount */}
          <div
            style={{
              padding: "5px 12px",
              borderRadius: "8px",
              background: account2 ? "#F5F3FF" : "#F8FAFC",
              border: account2
                ? `1.5px solid ${THEME.accent}`
                : "1px solid #E2E8F0",
              minHeight: "44px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              transition: "all 0.15s ease",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                lineHeight: 1.1,
                marginBottom: "3px",
              }}
            >
              <span
                style={{
                  fontSize: "10.5px",
                  fontWeight: 700,
                  color: account2 ? THEME.primaryDark : THEME.lightText,
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                }}
              >
                Account 2
              </span>
              {account2 && amount2 !== "" && !isNaN(Number(amount2)) && (
                <span
                  style={{
                    fontSize: "10.5px",
                    fontWeight: 700,
                    color: THEME.primary,
                  }}
                >
                  ₹{Number(amount2).toFixed(2)}
                </span>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <select
                id="receipt-account-2"
                value={account2}
                onChange={(e) => {
                  const val = e.target.value;
                  setAccount2(val);
                  if (!val) {
                    setAmount2("");
                  } else {
                    setTimeout(() => {
                      amount2Ref.current?.focus();
                    }, 50);
                  }
                }}
                style={{
                  ...inputBaseStyle,
                  height: "26px",
                  minHeight: "26px",
                  fontSize: "12px",
                  padding: "0 8px",
                  flex: "1 1 60%",
                  minWidth: "90px",
                  backgroundColor: "#FFFFFF",
                  borderColor: account2 ? THEME.accent : "#CBD5E1",
                  cursor: "pointer",
                }}
              >
                <option value="">-- Choose Account --</option>
                {accountOptions.map((acc, idx) => {
                  const accName = typeof acc === "string" ? acc : acc.name;
                  const accId =
                    typeof acc === "object" && (acc._id || acc.id)
                      ? acc._id || acc.id
                      : accName;
                  const accKey =
                    (typeof acc === "object" && (acc._id || acc.id)) ||
                    `${accName}-${idx}`;
                  return (
                    <option key={accKey} value={accId}>
                      {accName}
                    </option>
                  );
                })}
              </select>

              <input
                ref={amount2Ref}
                id="receipt-amount-2"
                type="number"
                min="0"
                step="any"
                disabled={!account2}
                placeholder={account2 ? "0.00" : "Amount"}
                value={amount2}
                onChange={(e) => setAmount2(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSaveBill();
                  }
                }}
                title={
                  account2
                    ? "Enter amount for selected account"
                    : "Select an account first"
                }
                style={{
                  ...inputBaseStyle,
                  height: "26px",
                  minHeight: "26px",
                  fontSize: "12.5px",
                  padding: "0 8px",
                  flex: "0 0 75px",
                  width: "75px",
                  backgroundColor: account2 ? "#FFFFFF" : "#F1F5F9",
                  cursor: account2 ? "text" : "not-allowed",
                  borderColor: account2 ? "#CBD5E1" : "#E2E8F0",
                  color: account2 ? "#1E293B" : "#94A3B8",
                }}
                onFocus={(e) => {
                  if (account2) {
                    e.target.style.borderColor = THEME.accent;
                    e.target.style.boxShadow =
                      "0 0 0 2px rgba(109,40,217,0.12)";
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#CBD5E1";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>
          </div>

          {/* Net Payment Highlight Box (Left Side of Save Bill Button) */}
          <div
            style={{
              padding: "6px 14px",
              borderRadius: "8px",
              background: "linear-gradient(135deg, #FAF9FF, #F5F3FF)",
              border: `1.5px solid ${THEME.accent}`,
              minHeight: "44px",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: THEME.primaryDark,
                  letterSpacing: "0.4px",
                  textTransform: "uppercase",
                  lineHeight: 1.1,
                }}
              >
                Final Payable
              </div>
            </div>

            <div
              style={{
                fontSize: "19px",
                fontWeight: 800,
                color: THEME.primary,
                letterSpacing: "-0.3px",
                whiteSpace: "nowrap",
                lineHeight: 1.1,
              }}
            >
              ₹{netPayment.toFixed(2)}
            </div>
          </div>

          {/* Save Bill Button on Right Side */}
          <button
            type="button"
            onClick={handleSaveBill}
            disabled={savingBill}
            style={{
              height: "44px",
              padding: "0 18px",
              borderRadius: "8px",
              border: "none",
              background: savingBill
                ? "#94A3B8"
                : "linear-gradient(135deg, #6D28D9, #8B5CF6)",
              color: "#FFFFFF",
              fontSize: "13.5px",
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              cursor: savingBill ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
              boxShadow: savingBill
                ? "none"
                : "0 3px 10px rgba(109,40,217,0.25)",
              transition: "all 0.15s ease",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              if (!savingBill) {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow =
                  "0 5px 14px rgba(109,40,217,0.32)";
              }
            }}
            onMouseLeave={(e) => {
              if (!savingBill) {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 3px 10px rgba(109,40,217,0.25)";
              }
            }}
          >
            <SaveIcon style={{ fontSize: "17px" }} />
            {savingBill ? "Saving Bill..." : "Save Bill (F4)"}
          </button>
        </div>
      </div>

      {/* =========================================================
          KEYBOARD SHORTCUTS MODAL
      ========================================================== */}
      {showShortcutsModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
            backdropFilter: "blur(2px)",
          }}
          onClick={() => setShowShortcutsModal(false)}
        >
          <div
            style={{
              ...cardStyle,
              maxWidth: "440px",
              width: "90%",
              overflow: "hidden",
              boxShadow: "0 20px 40px rgba(15, 23, 42, 0.16)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "14px 18px",
                borderBottom: "1px solid #EDE9FE",
                background: THEME.lighter,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "9px" }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "7px",
                    background: THEME.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <KeyboardIcon
                    style={{ fontSize: "18px", color: THEME.primary }}
                  />
                </div>
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color: THEME.text,
                  }}
                >
                  Keyboard Shortcuts
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowShortcutsModal(false)}
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "6px",
                  border: "1px solid #E2E8F0",
                  background: "#FFFFFF",
                  color: THEME.muted,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <CloseOutlined style={{ fontSize: "16px" }} />
              </button>
            </div>

            {/* Modal Body */}
            <div
              style={{
                padding: "18px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #F1F5F9",
                  fontSize: "13.5px",
                }}
              >
                <span
                  style={{
                    padding: "3px 8px",
                    borderRadius: "5px",
                    background: "#F1F5F9",
                    border: "1px solid #E2E8F0",
                    fontWeight: 700,
                    color: THEME.text,
                    fontSize: "12px",
                  }}
                >
                  ↑ / ↓
                </span>
                <span style={{ color: THEME.muted }}>
                  Full row focus navigation
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #F1F5F9",
                  fontSize: "13.5px",
                }}
              >
                <span
                  style={{
                    padding: "3px 8px",
                    borderRadius: "5px",
                    background: "#F1F5F9",
                    border: "1px solid #E2E8F0",
                    fontWeight: 700,
                    color: THEME.text,
                    fontSize: "12px",
                  }}
                >
                  Enter on Row
                </span>
                <span style={{ color: THEME.muted }}>
                  Focus first column to edit
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #F1F5F9",
                  fontSize: "13.5px",
                }}
              >
                <span
                  style={{
                    padding: "3px 8px",
                    borderRadius: "5px",
                    background: "#F1F5F9",
                    border: "1px solid #E2E8F0",
                    fontWeight: 700,
                    color: THEME.text,
                    fontSize: "12px",
                  }}
                >
                  ← / →
                </span>
                <span style={{ color: THEME.muted }}>
                  Navigate columns in row
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #F1F5F9",
                  fontSize: "13.5px",
                }}
              >
                <span
                  style={{
                    padding: "3px 8px",
                    borderRadius: "5px",
                    background: "#F1F5F9",
                    border: "1px solid #E2E8F0",
                    fontWeight: 700,
                    color: THEME.text,
                    fontSize: "12px",
                  }}
                >
                  Tab / Shift + Tab
                </span>
                <span style={{ color: THEME.muted }}>
                  Move between fields & cells
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #F1F5F9",
                  fontSize: "13.5px",
                }}
              >
                <span
                  style={{
                    padding: "3px 8px",
                    borderRadius: "5px",
                    background: THEME.light,
                    border: `1px solid ${THEME.border}`,
                    fontWeight: 700,
                    color: THEME.primary,
                    fontSize: "12px",
                  }}
                >
                  F2 / Alt + N
                </span>
                <span style={{ color: THEME.muted }}>Add New Item Row</span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #F1F5F9",
                  fontSize: "13.5px",
                }}
              >
                <span
                  style={{
                    padding: "3px 8px",
                    borderRadius: "5px",
                    background: THEME.light,
                    border: `1px solid ${THEME.border}`,
                    fontWeight: 700,
                    color: THEME.primary,
                    fontSize: "12px",
                  }}
                >
                  Ctrl + N
                </span>
                <span style={{ color: THEME.muted }}>
                  Add New Product (in modal / active row)
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #F1F5F9",
                  fontSize: "13.5px",
                }}
              >
                <span
                  style={{
                    padding: "3px 8px",
                    borderRadius: "5px",
                    background: THEME.light,
                    border: `1px solid ${THEME.border}`,
                    fontWeight: 700,
                    color: THEME.primary,
                    fontSize: "12px",
                  }}
                >
                  F4 / Alt + S
                </span>
                <span style={{ color: THEME.muted }}>
                  Save & Finalize Purchase Bill
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #F1F5F9",
                  fontSize: "13.5px",
                }}
              >
                <span
                  style={{
                    padding: "3px 8px",
                    borderRadius: "5px",
                    background: "#F1F5F9",
                    border: "1px solid #E2E8F0",
                    fontWeight: 700,
                    color: THEME.text,
                    fontSize: "12px",
                  }}
                >
                  Escape
                </span>
                <span style={{ color: THEME.muted }}>Return focus to row</span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #F1F5F9",
                  fontSize: "13.5px",
                }}
              >
                <span
                  style={{
                    padding: "3px 8px",
                    borderRadius: "5px",
                    background: THEME.dangerBg,
                    border: `1px solid ${THEME.dangerBorder}`,
                    fontWeight: 700,
                    color: THEME.danger,
                    fontSize: "12px",
                  }}
                >
                  Shift + Backspace
                </span>
                <span style={{ color: THEME.muted }}>
                  Delete current row from any cell
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #F1F5F9",
                  fontSize: "13.5px",
                }}
              >
                <span
                  style={{
                    padding: "3px 8px",
                    borderRadius: "5px",
                    background: "#F1F5F9",
                    border: "1px solid #E2E8F0",
                    fontWeight: 700,
                    color: THEME.text,
                    fontSize: "12px",
                  }}
                >
                  Space / R / %
                </span>
                <span style={{ color: THEME.muted }}>
                  Switch % and Rs discount
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  fontSize: "13.5px",
                }}
              >
                <span
                  style={{
                    padding: "3px 8px",
                    borderRadius: "5px",
                    background: "#F1F5F9",
                    border: "1px solid #E2E8F0",
                    fontWeight: 700,
                    color: THEME.text,
                    fontSize: "12px",
                  }}
                >
                  F1
                </span>
                <span style={{ color: THEME.muted }}>
                  Toggle Shortcuts Guide
                </span>
              </div>

              <button
                type="button"
                onClick={() => setShowShortcutsModal(false)}
                style={{
                  marginTop: "8px",
                  height: "38px",
                  borderRadius: "7px",
                  border: "none",
                  background: "linear-gradient(135deg, #6D28D9, #8B5CF6)",
                  color: "#FFFFFF",
                  fontSize: "14px",
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: "0 3px 9px rgba(109,40,217,0.22)",
                }}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          SUPPLIER SELECTION POPUP MODAL
      ========================================================== */}
      {supplierModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1400,
            backdropFilter: "blur(2px)",
          }}
          onClick={handleCloseSupplierModal}
        >
          <div
            style={{
              ...cardStyle,
              maxWidth: showAddSupplierView ? "820px" : "540px",
              width: "92%",
              height: showAddSupplierView ? "85vh" : "auto",
              maxHeight: "88vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 40px rgba(15, 23, 42, 0.18)",
              transition: "max-width 0.2s ease, height 0.2s ease",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                minHeight: "52px",
                padding: "0 18px",
                borderBottom: "1px solid #EDE9FE",
                background: THEME.lighter,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "9px" }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "7px",
                    background: THEME.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <StorefrontOutlined
                    style={{ fontSize: "18px", color: THEME.primary }}
                  />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: 700,
                      color: THEME.text,
                    }}
                  >
                    {showAddSupplierView
                      ? "Create Ledger / Add Supplier"
                      : "Select Supplier / Party"}
                  </div>
                  <div style={{ fontSize: "12px", color: THEME.lightText }}>
                    {showAddSupplierView
                      ? "Enter ledger details to create and automatically select supplier"
                      : "Search or use Up/Down arrows to choose a supplier"}
                  </div>
                </div>
              </div>

              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {showAddSupplierView && (
                  <button
                    type="button"
                    onClick={() => setShowAddSupplierView(false)}
                    style={{
                      padding: "5px 10px",
                      borderRadius: "6px",
                      border: "1px solid #E2E8F0",
                      background: "#FFFFFF",
                      color: THEME.muted,
                      fontSize: "12px",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      cursor: "pointer",
                    }}
                  >
                    <ArrowBackOutlined style={{ fontSize: "14px" }} />
                    Back to List
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleCloseSupplierModal}
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "6px",
                    border: "1px solid #E2E8F0",
                    background: "#FFFFFF",
                    color: THEME.muted,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <CloseOutlined style={{ fontSize: "16px" }} />
                </button>
              </div>
            </div>

            {/* Search Input Bar (Shown only in list mode) */}
            {!showAddSupplierView && (
              <div
                style={{
                  padding: "12px 18px",
                  borderBottom: "1px solid #F1F5F9",
                  background: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <div style={{ position: "relative", flex: 1 }}>
                  <SearchOutlined
                    style={{
                      position: "absolute",
                      left: "11px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "18px",
                      color: THEME.primary,
                      pointerEvents: "none",
                    }}
                  />
                  <input
                    ref={supplierSearchInputRef}
                    className="supplier-search-input"
                    autoFocus
                    style={{
                      ...inputBaseStyle,
                      height: "38px",
                      paddingLeft: "36px",
                      fontSize: "14px",
                    }}
                    placeholder="Type to filter suppliers by name, code, type..."
                    value={supplierSearch}
                    onChange={(e) => setSupplierSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    onFocus={(e) => {
                      e.target.style.backgroundColor = "#FEF08A";
                      e.target.style.borderColor = "#EAB308";
                      e.target.style.boxShadow =
                        "0 0 0 2px rgba(234, 179, 8, 0.25)";
                    }}
                    onBlur={(e) => {
                      e.target.style.backgroundColor = "#FFFFFF";
                      e.target.style.borderColor = "#E2E8F0";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                </div>

                {/* Add New Supplier Button on the Right End */}
                <button
                  type="button"
                  onClick={() => {
                    setShowAddSupplierView(true);
                  }}
                  title="Add New Supplier (Ctrl+N)"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    height: "38px",
                    padding: "0 12px",
                    borderRadius: "7px",
                    border: `1px solid ${THEME.primary}`,
                    background: THEME.light,
                    color: THEME.primary,
                    fontSize: "12.5px",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = THEME.primary;
                    e.currentTarget.style.color = "#FFFFFF";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = THEME.light;
                    e.currentTarget.style.color = THEME.primary;
                  }}
                >
                  <AddIcon style={{ fontSize: "16px" }} />
                  New
                  <span
                    style={{
                      fontSize: "10.5px",
                      padding: "1px 5px",
                      background: "rgba(109,40,217,0.12)",
                      borderRadius: "4px",
                      marginLeft: "2px",
                      fontWeight: 600,
                      color: THEME.primaryDark,
                    }}
                  >
                    Ctrl+N
                  </span>
                </button>
              </div>
            )}

            {/* Results List (Shown only in list mode) */}
            {!showAddSupplierView && (
              <div
                ref={supplierListContainerRef}
                style={{
                  maxHeight: "320px",
                  overflowY: "auto",
                  padding: "8px 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                {filteredSuppliers.length === 0 ? (
                  <div
                    style={{
                      padding: "36px 20px",
                      textAlign: "center",
                      color: THEME.muted,
                      fontSize: "14px",
                    }}
                  >
                    <StorefrontOutlined
                      style={{
                        fontSize: "32px",
                        color: THEME.lightText,
                        marginBottom: "6px",
                      }}
                    />
                    <div>
                      {supplierSearch.trim()
                        ? `No suppliers matching "${supplierSearch}"`
                        : "No suppliers found. Create your first supplier!"}
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddSupplierView(true)}
                      style={{
                        marginTop: "12px",
                        padding: "6px 14px",
                        borderRadius: "6px",
                        background: THEME.primary,
                        color: "#FFFFFF",
                        border: "none",
                        fontWeight: 600,
                        fontSize: "13px",
                        cursor: "pointer",
                      }}
                    >
                      + Add New Supplier (Ctrl+N)
                    </button>
                  </div>
                ) : (
                  filteredSuppliers.map((supplier, idx) => {
                    const isHighlighted = idx === highlightedSupplierIndex;
                    const isSelected =
                      (selectedSupplier?._id || selectedSupplier?.id) ===
                      (supplier._id || supplier.id);

                    return (
                      <div
                        key={supplier._id || supplier.id || idx}
                        data-supplier-index={idx}
                        onClick={() => handleSelectSupplier(supplier)}
                        onMouseEnter={() => setHighlightedSupplierIndex(idx)}
                        style={{
                          padding: "10px 12px",
                          borderRadius: "8px",
                          cursor: "pointer",
                          background: isHighlighted ? THEME.light : "#FFFFFF",
                          border: isHighlighted
                            ? `1px solid ${THEME.border}`
                            : "1px solid transparent",
                          boxShadow: isHighlighted
                            ? "0 2px 8px rgba(109,40,217,0.06)"
                            : "none",
                          transition: "all 0.12s ease",
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: 700,
                              color: isHighlighted ? THEME.primary : THEME.text,
                            }}
                          >
                            {supplier.name}
                          </span>

                          {isSelected && (
                            <span
                              style={{
                                padding: "2px 7px",
                                borderRadius: "6px",
                                background: "#DCFCE7",
                                color: "#15803D",
                                fontSize: "11px",
                                fontWeight: 700,
                                letterSpacing: "0.2px",
                              }}
                            >
                              SELECTED
                            </span>
                          )}
                        </div>

                        {(supplier.station || supplier.address) && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: "14px",
                              fontSize: "12px",
                              color: THEME.muted,
                            }}
                          >
                            {supplier.station && (
                              <span>
                                <strong style={{ color: "#64748B" }}>
                                  Station:
                                </strong>{" "}
                                <span
                                  style={{
                                    color: "#1E293B",
                                    textTransform: "uppercase",
                                    fontWeight: 500,
                                  }}
                                >
                                  {supplier.station}
                                </span>
                              </span>
                            )}

                            {supplier.address && (
                              <span>
                                <strong style={{ color: "#64748B" }}>
                                  Address:
                                </strong>{" "}
                                <span style={{ color: "#334155" }}>
                                  {supplier.address}
                                </span>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Modal Footer / Navigation Instructions (Shown only in list mode) */}
            {!showAddSupplierView && (
              <div
                style={{
                  padding: "10px 18px",
                  borderTop: "1px solid #F1F5F9",
                  background: "#F8FAFC",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: "11px",
                  color: THEME.muted,
                }}
              >
                <div>
                  Use <strong style={{ color: THEME.text }}>↑</strong> /{" "}
                  <strong style={{ color: THEME.text }}>↓</strong> to navigate •{" "}
                  <strong style={{ color: THEME.text }}>Enter</strong> to select
                  • <strong style={{ color: THEME.primary }}>Ctrl+N</strong> to
                  add new • <strong style={{ color: THEME.text }}>Esc</strong>{" "}
                  to close
                </div>
                <div>
                  {filteredSuppliers.length}{" "}
                  {filteredSuppliers.length === 1 ? "supplier" : "suppliers"}
                </div>
              </div>
            )}

            {/* =========================================================
                ADD NEW SUPPLIER / CREATE LEDGER IN-MODAL FORM (MARG ERP)
            ========================================================== */}
            {showAddSupplierView && (
              <form
                ref={supplierFormRef}
                className="supplier-modal-form"
                onSubmit={handleCreateSupplier}
                onKeyDown={handleSupplierFormKeyDown}
                onFocus={handleSupplierFormFocus}
                onBlur={handleSupplierFormBlur}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  overflow: "hidden",
                  minHeight: 0,
                  background: "#F8FAFC",
                }}
              >
                <style>{`
                  .supplier-modal-form input:focus,
                  .supplier-modal-form select:focus,
                  .supplier-modal-form textarea:focus {
                    background-color: #FEF08A !important;
                    border-color: #EAB308 !important;
                    box-shadow: 0 0 0 2px rgba(234, 179, 8, 0.25) !important;
                    outline: none !important;
                  }
                `}</style>

                {/* Scrollable Form Body Container */}
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "16px 20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                  }}
                >
                  {/* 1. Account & Ledger Details Card */}
                  <div
                    style={{
                      background: "#FFFFFF",
                      border: `1px solid ${THEME.border}`,
                      borderRadius: "10px",
                      padding: "14px 16px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: THEME.primary,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "10px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <StorefrontOutlined style={{ fontSize: "16px" }} />
                      Account & Ledger Details
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr",
                        gap: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      {/* LEDGER NAME */}
                      <div>
                        <label style={formLabelStyle}>
                          Ledger Name{" "}
                          <span style={{ color: THEME.danger }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={newSupplierData.name}
                          onChange={handleNewSupplierChange}
                          autoFocus
                          placeholder="Enter ledger / party name (e.g. RANBAXY LABS LTD)"
                          required
                          style={{
                            ...inputBaseStyle,
                            height: "34px",
                            fontSize: "13px",
                            borderColor: THEME.primary,
                            fontWeight: 600,
                          }}
                        />
                      </div>

                      {/* STATION */}
                      <div>
                        <label style={formLabelStyle}>Station</label>
                        <input
                          type="text"
                          name="station"
                          value={newSupplierData.station}
                          onChange={handleNewSupplierChange}
                          placeholder="e.g. GURGAON / GUWAHATI"
                          style={{
                            ...inputBaseStyle,
                            height: "34px",
                            fontSize: "12.5px",
                          }}
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1.2fr 1fr 1fr",
                        gap: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      {/* ACCOUNT GROUP */}
                      <div>
                        <label style={formLabelStyle}>Account Group</label>
                        <select
                          name="accountGroup"
                          value={newSupplierData.accountGroup}
                          onChange={handleNewSupplierChange}
                          onFocus={(e) => openSelectPicker(e.target)}
                          style={{
                            ...inputBaseStyle,
                            height: "34px",
                            fontSize: "12.5px",
                          }}
                        >
                          {groups && groups.length > 0 ? (
                            groups.map((group) => {
                              const gName = group?.name;
                              const gId = group?._id;
                              return (
                                <option key={gId} value={gId}>
                                  {gName}
                                </option>
                              );
                            })
                          ) : (
                            <></>
                          )}
                        </select>
                      </div>

                      {/* BALANCING METHOD */}
                      <div>
                        <label style={formLabelStyle}>Balancing Method</label>
                        <select
                          name="balancingMethod"
                          value={newSupplierData.balancingMethod}
                          onChange={handleNewSupplierChange}
                          onFocus={(e) => openSelectPicker(e.target)}
                          style={{
                            ...inputBaseStyle,
                            height: "34px",
                            fontSize: "12.5px",
                          }}
                        >
                          <option value="Bill by Bill">Bill by Bill</option>
                          <option value="On Account">On Account</option>
                          <option value="FIFO Base">FIFO Base</option>
                        </select>
                      </div>

                      {/* OPENING BAL & CR/DR */}
                      <div>
                        <label style={formLabelStyle}>Opening Balance</label>
                        <div style={{ display: "flex", gap: "4px" }}>
                          <input
                            type="number"
                            step="0.01"
                            name="openingBalance"
                            value={newSupplierData.openingBalance}
                            onChange={handleNewSupplierChange}
                            placeholder="0.00"
                            style={{
                              ...inputBaseStyle,
                              height: "34px",
                              fontSize: "12.5px",
                              flex: 1,
                            }}
                          />
                          <select
                            name="openingType"
                            value={newSupplierData.openingType}
                            onChange={handleNewSupplierChange}
                            onFocus={(e) => openSelectPicker(e.target)}
                            style={{
                              ...inputBaseStyle,
                              height: "34px",
                              fontSize: "12.5px",
                              width: "60px",
                              padding: "0 4px",
                            }}
                          >
                            <option value="Cr">Cr</option>
                            <option value="Dr">Dr</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1.2fr 1fr",
                        gap: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      {/* DISPLAY NAME (MAIL TO) */}
                      <div>
                        <label style={formLabelStyle}>Display Name</label>
                        <input
                          type="text"
                          name="mailTo"
                          value={newSupplierData.mailTo}
                          onChange={handleNewSupplierChange}
                          placeholder="Display Name"
                          style={{
                            ...inputBaseStyle,
                            height: "34px",
                            fontSize: "12.5px",
                          }}
                        />
                      </div>

                      {/* PIN CODE */}
                      <div>
                        <label style={formLabelStyle}>Pin Code</label>
                        <input
                          type="text"
                          name="pinCode"
                          value={newSupplierData.pinCode}
                          onChange={handleNewSupplierChange}
                          placeholder="e.g. 122001"
                          maxLength={10}
                          style={{
                            ...inputBaseStyle,
                            height: "34px",
                            fontSize: "12.5px",
                          }}
                        />
                      </div>
                    </div>

                    {/* ADDRESS */}
                    <div>
                      <label style={formLabelStyle}>Address</label>
                      <textarea
                        name="address"
                        value={newSupplierData.address}
                        onChange={handleNewSupplierChange}
                        rows={2}
                        placeholder="Shop/Premises No., Street, Area, City..."
                        style={{
                          ...inputBaseStyle,
                          height: "auto",
                          minHeight: "52px",
                          padding: "6px 10px",
                          fontSize: "12.5px",
                          resize: "vertical",
                          fontFamily: "inherit",
                        }}
                      />
                    </div>
                  </div>

                  {/* 2. Contact & Communication Card */}
                  <div
                    style={{
                      background: "#FFFFFF",
                      border: `1px solid ${THEME.cardBorder}`,
                      borderRadius: "10px",
                      padding: "14px 16px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#334155",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "10px",
                      }}
                    >
                      Contact & Communication
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      {/* CONTACT PERSON */}
                      <div>
                        <label style={formLabelStyle}>Contact Person</label>
                        <input
                          type="text"
                          name="contactPerson"
                          value={newSupplierData.contactPerson}
                          onChange={handleNewSupplierChange}
                          placeholder="Name of contact"
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        />
                      </div>

                      {/* DESIGNATION */}
                      <div>
                        <label style={formLabelStyle}>Designation</label>
                        <input
                          type="text"
                          name="designation"
                          value={newSupplierData.designation}
                          onChange={handleNewSupplierChange}
                          placeholder="e.g. Sales Executive / Manager"
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      {/* MOBILE */}
                      <div>
                        <label style={formLabelStyle}>Mobile</label>
                        <input
                          type="text"
                          name="mobile"
                          value={newSupplierData.mobile}
                          onChange={handleNewSupplierChange}
                          placeholder="e.g. 9876543210"
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        />
                      </div>

                      {/* PHONE OFF */}
                      <div>
                        <label style={formLabelStyle}>Phone No. (Off.)</label>
                        <input
                          type="text"
                          name="phoneOff"
                          value={newSupplierData.phoneOff}
                          onChange={handleNewSupplierChange}
                          placeholder="Office telephone"
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        />
                      </div>

                      {/* PHONE RES */}
                      <div>
                        <label style={formLabelStyle}>Phone No. (Res.)</label>
                        <input
                          type="text"
                          name="phoneRes"
                          value={newSupplierData.phoneRes}
                          onChange={handleNewSupplierChange}
                          placeholder="Residence phone"
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "10px",
                      }}
                    >
                      {/* EMAIL */}
                      <div>
                        <label style={formLabelStyle}>E-Mail</label>
                        <input
                          type="email"
                          name="email"
                          value={newSupplierData.email}
                          onChange={handleNewSupplierChange}
                          placeholder="supplier@domain.com"
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        />
                      </div>

                      {/* WEBSITE */}
                      <div>
                        <label style={formLabelStyle}>Web Site</label>
                        <input
                          type="text"
                          name="website"
                          value={newSupplierData.website}
                          onChange={handleNewSupplierChange}
                          placeholder="www.supplier.com"
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* 3. Statutory, GST & License Details Card */}
                  <div
                    style={{
                      background: "#FFFFFF",
                      border: `1px solid ${THEME.cardBorder}`,
                      borderRadius: "10px",
                      padding: "14px 16px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#334155",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "10px",
                      }}
                    >
                      Statutory & Licensing (Tax / Drug Licenses)
                    </div>

                    {/* GST & PAN */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1.5fr 1fr 1.2fr",
                        gap: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      <div>
                        <label style={formLabelStyle}>GST Heading</label>
                        <select
                          name="gstHeading"
                          value={newSupplierData.gstHeading}
                          onChange={handleNewSupplierChange}
                          onFocus={(e) => openSelectPicker(e.target)}
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        >
                          <option value="Local">Local</option>
                          <option value="Central">Central</option>
                          <option value="Interstate">Interstate</option>
                          <option value="None">None</option>
                        </select>
                      </div>

                      <div>
                        <label style={formLabelStyle}>GSTIN No.</label>
                        <input
                          type="text"
                          name="gstin"
                          value={newSupplierData.gstin}
                          onChange={handleNewSupplierChange}
                          placeholder="15-digit GSTIN (e.g. 18AAAAA0000A1Z5)"
                          maxLength={15}
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                            textTransform: "uppercase",
                          }}
                        />
                      </div>

                      <div>
                        <label style={formLabelStyle}>GSTIN Dt.</label>
                        <input
                          type="date"
                          name="gstinDate"
                          value={newSupplierData.gstinDate}
                          onChange={handleNewSupplierChange}
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        />
                      </div>

                      <div>
                        <label style={formLabelStyle}>I.T. PAN No.</label>
                        <input
                          type="text"
                          name="panNo"
                          value={newSupplierData.panNo}
                          onChange={handleNewSupplierChange}
                          placeholder="10-digit PAN (e.g. ABCDE1234F)"
                          maxLength={10}
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                            textTransform: "uppercase",
                          }}
                        />
                      </div>
                    </div>

                    {/* DRUG LICENSE */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr",
                        gap: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      <div>
                        <label style={formLabelStyle}>
                          D.L. No. (Drug License)
                        </label>
                        <input
                          type="text"
                          name="dlNo"
                          value={newSupplierData.dlNo}
                          onChange={handleNewSupplierChange}
                          placeholder="Drug license number(s) e.g. 20B/21B"
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        />
                      </div>

                      <div>
                        <label style={formLabelStyle}>D.L. Exp</label>
                        <input
                          type="date"
                          name="dlExp"
                          value={newSupplierData.dlExp}
                          onChange={handleNewSupplierChange}
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        />
                      </div>
                    </div>

                    {/* FOOD LICENSE */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1.5fr 1fr",
                        gap: "10px",
                      }}
                    >
                      <div>
                        <label style={formLabelStyle}>Food Heading</label>
                        <input
                          type="text"
                          name="foodHeading"
                          value={newSupplierData.foodHeading}
                          onChange={handleNewSupplierChange}
                          placeholder="Food Licence"
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        />
                      </div>
                      <div>
                        <label style={formLabelStyle}>Food Licence No.</label>
                        <input
                          type="text"
                          name="foodLicenceNo"
                          value={newSupplierData.foodLicenceNo}
                          onChange={handleNewSupplierChange}
                          placeholder="FSSAI / Food Lic No."
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        />
                      </div>
                      <div>
                        <label style={formLabelStyle}>Food Lic. Exp</label>
                        <input
                          type="date"
                          name="foodExp"
                          value={newSupplierData.foodExp}
                          onChange={handleNewSupplierChange}
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* 4. Classification & Regional Settings Card */}
                  <div
                    style={{
                      background: "#FFFFFF",
                      border: `1px solid ${THEME.cardBorder}`,
                      borderRadius: "10px",
                      padding: "14px 16px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#334155",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "10px",
                      }}
                    >
                      Classification & Marg ERP Settings
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1.2fr 1fr 1fr",
                        gap: "10px",
                      }}
                    >
                      {/* STATE */}
                      <div>
                        <label style={formLabelStyle}>State</label>
                        <select
                          name="state"
                          value={newSupplierData.state}
                          onChange={handleNewSupplierChange}
                          onFocus={(e) => openSelectPicker(e.target)}
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        >
                          <option value="18-ASSAM">18-ASSAM</option>
                          <option value="06-HARYANA">06-HARYANA</option>
                          <option value="07-DELHI">07-DELHI</option>
                          <option value="09-UTTAR PRADESH">
                            09-UTTAR PRADESH
                          </option>
                          <option value="19-WEST BENGAL">19-WEST BENGAL</option>
                          <option value="27-MAHARASHTRA">27-MAHARASHTRA</option>
                          <option value="24-GUJARAT">24-GUJARAT</option>
                          <option value="33-TAMIL NADU">33-TAMIL NADU</option>
                          <option value="29-KARNATAKA">29-KARNATAKA</option>
                          <option value="36-TELANGANA">36-TELANGANA</option>
                          <option value="12-ARUNACHAL PRADESH">
                            12-ARUNACHAL PRADESH
                          </option>
                          <option value="17-MEGHALAYA">17-MEGHALAYA</option>
                          <option value="14-MANIPUR">14-MANIPUR</option>
                          <option value="15-MIZORAM">15-MIZORAM</option>
                          <option value="13-NAGALAND">13-NAGALAND</option>
                          <option value="16-TRIPURA">16-TRIPURA</option>
                        </select>
                      </div>

                      {/* LEDGER TYPE */}
                      <div>
                        <label style={formLabelStyle}>Ledger Type</label>
                        <select
                          name="ledgerType"
                          value={newSupplierData.ledgerType}
                          onChange={handleNewSupplierChange}
                          onFocus={(e) => openSelectPicker(e.target)}
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        >
                          <option value="Assets">Assets</option>
                          <option value="Liabilities">Liabilities</option>
                          <option value="Equity">Equity</option>
                          <option value="Operating Revenue">
                            Operating Revenue
                          </option>
                          <option value="Operating Expenses">
                            Operating Expenses
                          </option>
                        </select>
                      </div>

                      {/* LEDGER DATE */}
                      <div>
                        <label style={formLabelStyle}>Ledger Date</label>
                        <input
                          type="date"
                          name="ledgerDate"
                          value={newSupplierData.ledgerDate}
                          onChange={handleNewSupplierChange}
                          style={{
                            ...inputBaseStyle,
                            height: "32px",
                            fontSize: "12px",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 5. Fixed & Sticky Modal Footer Action Buttons */}
                <div
                  style={{
                    flexShrink: 0,
                    padding: "12px 20px",
                    borderTop: "1px solid #E2E8F0",
                    background: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: "0 -4px 12px rgba(15, 23, 42, 0.04)",
                    zIndex: 10,
                  }}
                >
                  <button
                    type="button"
                    onClick={handleResetNewSupplier}
                    style={{
                      height: "38px",
                      padding: "0 14px",
                      borderRadius: "7px",
                      border: "1px solid #E2E8F0",
                      background: "#FFFFFF",
                      color: THEME.muted,
                      fontSize: "13px",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <RestartAltOutlined style={{ fontSize: "16px" }} />
                    Reset
                  </button>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setShowAddSupplierView(false)}
                      style={{
                        height: "38px",
                        padding: "0 14px",
                        borderRadius: "7px",
                        border: "1px solid #E2E8F0",
                        background: "#FFFFFF",
                        color: THEME.text,
                        fontSize: "13px",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      ref={saveSupplierBtnRef}
                      type="submit"
                      disabled={savingNewSupplier}
                      style={{
                        height: "38px",
                        padding: "0 20px",
                        borderRadius: "7px",
                        border: "none",
                        background: savingNewSupplier
                          ? THEME.muted
                          : THEME.primary,
                        color: "#FFFFFF",
                        fontSize: "13px",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: savingNewSupplier ? "not-allowed" : "pointer",
                        boxShadow: "0 2px 8px rgba(109,40,217,0.25)",
                        outline: "none",
                        transition: "all 0.15s ease",
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.boxShadow =
                          "0 0 0 3px rgba(109,40,217,0.4), 0 4px 12px rgba(109,40,217,0.3)";
                        e.currentTarget.style.transform = "scale(1.02)";
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(109,40,217,0.25)";
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      <SaveIcon style={{ fontSize: "18px" }} />
                      {savingNewSupplier
                        ? "Creating..."
                        : "Save & Select Supplier"}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* =========================================================
          PRODUCT SELECTION POPUP MODAL
      ========================================================== */}
      {productModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1400,
            backdropFilter: "blur(2px)",
          }}
          onClick={handleCloseProductModal}
        >
          <div
            style={{
              ...cardStyle,
              maxWidth: "620px",
              width: "92%",
              maxHeight: "88vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 40px rgba(15, 23, 42, 0.18)",
              transition: "max-width 0.2s ease",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                minHeight: "52px",
                padding: "0 18px",
                borderBottom: "1px solid #EDE9FE",
                background: THEME.lighter,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "9px" }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "7px",
                    background: THEME.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Inventory2Outlined
                    style={{ fontSize: "18px", color: THEME.primary }}
                  />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: 700,
                      color: THEME.text,
                    }}
                  >
                    {showAddProductView
                      ? "Add New Product"
                      : "Select Product / Item"}
                  </div>
                  <div style={{ fontSize: "12px", color: THEME.lightText }}>
                    {showAddProductView
                      ? "Enter details to create and automatically select product"
                      : activeProductRowIndex !== null
                        ? `Row #${activeProductRowIndex + 1} • Search or use Up/Down arrows to choose`
                        : "Search or use Up/Down arrows to choose an item"}
                  </div>
                </div>
              </div>

              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {showAddProductView && (
                  <button
                    type="button"
                    onClick={() => setShowAddProductView(false)}
                    style={{
                      padding: "5px 10px",
                      borderRadius: "6px",
                      border: "1px solid #E2E8F0",
                      background: "#FFFFFF",
                      color: THEME.muted,
                      fontSize: "12px",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      cursor: "pointer",
                    }}
                  >
                    <ArrowBackOutlined style={{ fontSize: "14px" }} />
                    Back to List
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleCloseProductModal}
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "6px",
                    border: "1px solid #E2E8F0",
                    background: "#FFFFFF",
                    color: THEME.muted,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <CloseOutlined style={{ fontSize: "16px" }} />
                </button>
              </div>
            </div>

            {/* Search Input Bar (Shown only in search mode) */}
            {!showAddProductView && (
              <div
                style={{
                  padding: "12px 18px",
                  borderBottom: "1px solid #F1F5F9",
                  background: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {/* Search Text Input */}
                <div style={{ position: "relative", flex: 1 }}>
                  <SearchOutlined
                    style={{
                      position: "absolute",
                      left: "11px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "18px",
                      color: THEME.primary,
                      pointerEvents: "none",
                    }}
                  />
                  <input
                    ref={productSearchInputRef}
                    autoFocus
                    style={{
                      ...inputBaseStyle,
                      height: "38px",
                      paddingLeft: "36px",
                      paddingRight: loadingProducts ? "90px" : "12px",
                      fontSize: "14px",
                    }}
                    placeholder="Type to filter products by name, unit, tax..."
                    value={productSearchText}
                    onChange={(e) => setProductSearchText(e.target.value)}
                    onKeyDown={handleProductSearchKeyDown}
                    onFocus={(e) => {
                      e.target.style.borderColor = THEME.accent;
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(109,40,217,0.12)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#E2E8F0";
                      e.target.style.boxShadow = "none";
                    }}
                  />
                  {loadingProducts && (
                    <span
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: THEME.primary,
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      Searching...
                    </span>
                  )}
                </div>

                {/* Add New Product Button on the Right End */}
                <button
                  type="button"
                  onClick={() => {
                    setShowAddProductView(true);
                  }}
                  title="Add New Product (Ctrl+N)"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    height: "38px",
                    padding: "0 12px",
                    borderRadius: "7px",
                    border: `1px solid ${THEME.primary}`,
                    background: THEME.light,
                    color: THEME.primary,
                    fontSize: "12.5px",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = THEME.primary;
                    e.currentTarget.style.color = "#FFFFFF";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = THEME.light;
                    e.currentTarget.style.color = THEME.primary;
                  }}
                >
                  <AddIcon style={{ fontSize: "16px" }} />
                  New Product
                  <span
                    style={{
                      fontSize: "10.5px",
                      padding: "1px 5px",
                      background: "rgba(109,40,217,0.12)",
                      borderRadius: "4px",
                      marginLeft: "2px",
                      fontWeight: 600,
                      color: THEME.primaryDark,
                    }}
                  >
                    Ctrl+N
                  </span>
                </button>
              </div>
            )}

            {/* Results List (Shown only in search mode) */}
            {!showAddProductView && (
              <div
                ref={productListContainerRef}
                style={{
                  maxHeight: "340px",
                  overflowY: "auto",
                  padding: "8px 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                {productResults.length === 0 ? (
                  <div
                    style={{
                      padding: "36px 20px",
                      textAlign: "center",
                      color: THEME.muted,
                      fontSize: "14px",
                    }}
                  >
                    <Inventory2Outlined
                      style={{
                        fontSize: "32px",
                        color: THEME.lightText,
                        marginBottom: "6px",
                      }}
                    />
                    <div>
                      {loadingProducts
                        ? "Loading products from server..."
                        : productSearchText
                          ? `No products matching "${productSearchText}"`
                          : "No products available in database"}
                    </div>
                  </div>
                ) : (
                  productResults.map((product, idx) => {
                    const isHighlighted = idx === highlightedProductIndex;

                    const currentRowProduct =
                      activeProductRowIndex !== null
                        ? items[activeProductRowIndex]?.product
                        : null;

                    const currentProductId =
                      currentRowProduct?._id || currentRowProduct?.id;

                    const isSelected = currentProductId === product._id;

                    return (
                      <div
                        key={product._id}
                        data-product-index={idx}
                        onClick={() => handleSelectProduct(product)}
                        onMouseEnter={() => setHighlightedProductIndex(idx)}
                        style={{
                          padding: "10px 12px",
                          borderRadius: "8px",
                          cursor: "pointer",
                          background: isHighlighted ? THEME.light : "#FFFFFF",
                          border: isHighlighted
                            ? `1px solid ${THEME.border}`
                            : "1px solid transparent",
                          boxShadow: isHighlighted
                            ? "0 2px 8px rgba(109,40,217,0.06)"
                            : "none",
                          transition: "all 0.12s ease",
                          display: "flex",
                          flexDirection: "column",
                          gap: "7px",
                        }}
                      >
                        {/* PRODUCT NAME + SELECTED */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "10px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: 700,
                              color: isHighlighted ? THEME.primary : THEME.text,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {product.name}
                          </span>

                          {isSelected && (
                            <span
                              style={{
                                flexShrink: 0,
                                padding: "2px 7px",
                                borderRadius: "6px",
                                background: "#DCFCE7",
                                color: "#15803D",
                                fontSize: "10.5px",
                                fontWeight: 700,
                                letterSpacing: "0.3px",
                              }}
                            >
                              SELECTED
                            </span>
                          )}
                        </div>

                        {/* PRODUCT DETAILS */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "6px",
                          }}
                        >
                          {/* UNIT 1 */}
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "3px 7px",
                              borderRadius: "5px",
                              background: "#F8FAFC",
                              border: "1px solid #E2E8F0",
                              color: "#475569",
                              fontSize: "11px",
                              fontWeight: 600,
                            }}
                          >
                            <span style={{ color: "#94A3B8", fontWeight: 500 }}>
                              Unit 1
                            </span>
                            {product.unit1}
                          </span>

                          {/* UNIT 2 */}
                          {product.unit2 && (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "3px 7px",
                                borderRadius: "5px",
                                background: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                                color: "#475569",
                                fontSize: "11px",
                                fontWeight: 600,
                              }}
                            >
                              <span
                                style={{ color: "#94A3B8", fontWeight: 500 }}
                              >
                                Unit 2
                              </span>
                              {product.unit2}
                            </span>
                          )}

                          {/* PRIMARY UNIT */}
                          <span
                            style={{
                              padding: "3px 7px",
                              borderRadius: "5px",
                              background: "#F0FDF4",
                              border: "1px solid #BBF7D0",
                              color: "#15803D",
                              fontSize: "11px",
                              fontWeight: 700,
                            }}
                          >
                            Primary:{" "}
                            {product.primaryUnit === "unit2" && product.unit2
                              ? product.unit2
                              : product.unit1}
                          </span>

                          {/* CONVERSION */}
                          {product.unit2 && (
                            <span
                              style={{
                                padding: "3px 7px",
                                borderRadius: "5px",
                                background: "#FAF5FF",
                                border: "1px solid #E9D5FF",
                                color: "#7C3AED",
                                fontSize: "11px",
                                fontWeight: 600,
                              }}
                            >
                              1 {product.unit1} = {product.unitConversion || 1}{" "}
                              {product.unit2}
                            </span>
                          )}

                          {/* GST */}
                          <span
                            style={{
                              padding: "3px 7px",
                              borderRadius: "5px",
                              background: "#EEF2FF",
                              border: "1px solid #C7D2FE",
                              color: "#4338CA",
                              fontSize: "11px",
                              fontWeight: 600,
                            }}
                          >
                            GST: {product.tax ?? 0}%
                          </span>

                          {/* DECIMAL */}
                          <span
                            style={{
                              padding: "3px 7px",
                              borderRadius: "5px",
                              background: "1px solid #E2E8F0",
                              color: "#7C3AED",
                              fontSize: "11px",
                              fontWeight: 600,
                            }}
                          >
                            {product.currentStock}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Modal Footer / Navigation Instructions (Shown only in search mode) */}
            {!showAddProductView && (
              <div
                style={{
                  padding: "10px 18px",
                  borderTop: "1px solid #F1F5F9",
                  background: "#F8FAFC",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: "11px",
                  color: THEME.muted,
                }}
              >
                <div>
                  Use <strong style={{ color: THEME.text }}>↑</strong> /{" "}
                  <strong style={{ color: THEME.text }}>↓</strong> to navigate •{" "}
                  <strong style={{ color: THEME.text }}>Enter</strong> to select
                  • <strong style={{ color: THEME.primary }}>Ctrl+N</strong> to
                  add new • <strong style={{ color: THEME.text }}>Esc</strong>{" "}
                  to close
                </div>
                <div>
                  {productResults.length}{" "}
                  {productResults.length === 1 ? "product" : "products"}
                </div>
              </div>
            )}

            {/* =========================================================
                ADD NEW PRODUCT IN-MODAL FORM
            ========================================================== */}
            {showAddProductView && (
              <form
                onSubmit={handleCreateProduct}
                style={{
                  padding: "16px 20px",
                  overflowY: "auto",
                  maxHeight: "calc(88vh - 60px)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px",
                  background: "#FFFFFF",
                }}
              >
                {/* Product Name */}
                <div>
                  <label
                    style={{
                      ...labelStyle,
                      fontSize: "12.5px",
                      marginBottom: "4px",
                    }}
                  >
                    Product Name <span style={{ color: THEME.danger }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={newProductData.name}
                    onChange={handleNewProductChange}
                    autoFocus
                    placeholder="Enter product name (e.g. Paracetamol 500mg)"
                    required
                    style={{
                      ...inputBaseStyle,
                      height: "38px",
                      fontSize: "13.5px",
                    }}
                  />
                </div>

                {/* Unit Relationship */}
                <div
                  style={{
                    border: `1px solid ${THEME.border}`,
                    borderRadius: "8px",
                    background: "#FFFFFF",
                    padding: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "6px",
                          background: THEME.light,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <StraightenOutlined
                          style={{ fontSize: "16px", color: THEME.primary }}
                        />
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: "13px",
                            fontWeight: 700,
                            color: THEME.text,
                          }}
                        >
                          Unit Relationship
                        </div>
                        <div
                          style={{ fontSize: "11px", color: THEME.lightText }}
                        >
                          Define how the larger unit contains the smaller unit
                        </div>
                      </div>
                    </div>
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: "10px",
                        background: THEME.light,
                        color: THEME.primary,
                        fontSize: "10.5px",
                        fontWeight: 700,
                      }}
                    >
                      CONVERSION
                    </span>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    {/* Left: Unit 1 and Unit 2 */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <div>
                        <label
                          style={{
                            ...labelStyle,
                            fontSize: "11.5px",
                            marginBottom: "2px",
                          }}
                        >
                          Unit 1 <span style={{ color: THEME.danger }}>*</span>
                        </label>
                        <div
                          style={{
                            fontSize: "10.5px",
                            color: THEME.primary,
                            fontWeight: 600,
                            marginBottom: "3px",
                          }}
                        >
                          Larger / Parent Unit
                        </div>
                        <input
                          type="text"
                          name="unit1"
                          value={newProductData.unit1}
                          onChange={handleNewProductChange}
                          placeholder="e.g. Box"
                          required
                          style={{
                            ...inputBaseStyle,
                            height: "34px",
                            fontSize: "13px",
                          }}
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            ...labelStyle,
                            fontSize: "11.5px",
                            marginBottom: "2px",
                          }}
                        >
                          Unit 2{" "}
                          <span
                            style={{
                              fontSize: "10.5px",
                              color: THEME.lightText,
                              fontWeight: 500,
                            }}
                          >
                            (Optional)
                          </span>
                        </label>
                        <div
                          style={{
                            fontSize: "10.5px",
                            color: THEME.muted,
                            fontWeight: 600,
                            marginBottom: "3px",
                          }}
                        >
                          Smaller / Base Unit
                        </div>
                        <input
                          type="text"
                          name="unit2"
                          value={newProductData.unit2}
                          onChange={handleNewProductChange}
                          placeholder="e.g. Pcs"
                          style={{
                            ...inputBaseStyle,
                            height: "34px",
                            fontSize: "13px",
                          }}
                        />
                      </div>
                    </div>

                    {/* Right: Conversion Formula & Primary Unit */}
                    <div
                      style={{
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        background: "#F8FAFC",
                        padding: "10px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "11px",
                            fontWeight: 700,
                            color: THEME.text,
                            marginBottom: "6px",
                          }}
                        >
                          Conversion Formula
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                            background: "#FFFFFF",
                            padding: "8px",
                            borderRadius: "6px",
                            border: "1px solid #E2E8F0",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: THEME.text,
                            }}
                          >
                            1 {newProductData.unit1 || "Unit 1"} =
                          </span>
                          <input
                            type="number"
                            name="conversionQty"
                            value={
                              newProductData.unit2
                                ? newProductData.conversionQty
                                : "1"
                            }
                            onChange={handleNewProductChange}
                            min="1"
                            step={
                              newProductData.allowDecimal === "Yes"
                                ? "0.01"
                                : "1"
                            }
                            disabled={!newProductData.unit2}
                            placeholder="1"
                            style={{
                              width: "56px",
                              height: "28px",
                              textAlign: "center",
                              fontSize: "13px",
                              fontWeight: 700,
                              borderRadius: "5px",
                              border: !newProductData.unit2
                                ? "1px solid #E2E8F0"
                                : `1px solid ${THEME.primary}`,
                              background: !newProductData.unit2
                                ? "#F1F5F9"
                                : "#FFFFFF",
                              color: !newProductData.unit2
                                ? THEME.muted
                                : THEME.text,
                              cursor: !newProductData.unit2
                                ? "not-allowed"
                                : "text",
                              outline: "none",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: THEME.primary,
                            }}
                          >
                            {newProductData.unit2 ||
                              newProductData.unit1 ||
                              "Unit 1"}
                          </span>
                        </div>
                      </div>

                      <div style={{ marginTop: "8px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            marginBottom: "4px",
                          }}
                        >
                          <CalculateOutlined
                            style={{ fontSize: "14px", color: THEME.accent }}
                          />
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: 700,
                              color: THEME.text,
                            }}
                          >
                            Primary Unit
                          </span>
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: newProductData.unit2?.trim()
                              ? "1fr 1fr"
                              : "1fr",
                            gap: "6px",
                          }}
                        >
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "4px",
                              padding: "4px 8px",
                              borderRadius: "5px",
                              fontSize: "11.5px",
                              cursor: "pointer",
                              border:
                                newProductData.primaryUnit === "unit1"
                                  ? `1px solid ${THEME.primary}`
                                  : "1px solid #CBD5E1",
                              background:
                                newProductData.primaryUnit === "unit1"
                                  ? THEME.light
                                  : "#FFFFFF",
                              color:
                                newProductData.primaryUnit === "unit1"
                                  ? THEME.primary
                                  : THEME.muted,
                              fontWeight:
                                newProductData.primaryUnit === "unit1"
                                  ? 700
                                  : 500,
                            }}
                          >
                            <input
                              type="radio"
                              name="primaryUnit"
                              value="unit1"
                              checked={newProductData.primaryUnit === "unit1"}
                              onChange={handleNewProductChange}
                              style={{ display: "none" }}
                            />
                            {newProductData.unit1 || "Unit 1"}
                          </label>
                          {newProductData.unit2?.trim() && (
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "4px",
                                padding: "4px 8px",
                                borderRadius: "5px",
                                fontSize: "11.5px",
                                cursor: "pointer",
                                border:
                                  newProductData.primaryUnit === "unit2"
                                    ? `1px solid ${THEME.accent}`
                                    : "1px solid #CBD5E1",
                                background:
                                  newProductData.primaryUnit === "unit2"
                                    ? "#FAF9FF"
                                    : "#FFFFFF",
                                color:
                                  newProductData.primaryUnit === "unit2"
                                    ? THEME.accent
                                    : THEME.muted,
                                fontWeight:
                                  newProductData.primaryUnit === "unit2"
                                    ? 700
                                    : 500,
                              }}
                            >
                              <input
                                type="radio"
                                name="primaryUnit"
                                value="unit2"
                                checked={newProductData.primaryUnit === "unit2"}
                                onChange={handleNewProductChange}
                                style={{ display: "none" }}
                              />
                              {newProductData.unit2}
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tax & Decimal Row */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                  }}
                >
                  {/* Tax */}
                  <div>
                    <label
                      style={{
                        ...labelStyle,
                        fontSize: "12px",
                        marginBottom: "4px",
                      }}
                    >
                      Tax (%)
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type="number"
                        name="tax"
                        value={newProductData.tax}
                        onChange={handleNewProductChange}
                        placeholder="0"
                        min="0"
                        max="100"
                        step="0.01"
                        style={{
                          ...inputBaseStyle,
                          height: "36px",
                          paddingRight: "30px",
                          fontSize: "13px",
                        }}
                      />
                      <PercentOutlined
                        style={{
                          position: "absolute",
                          right: "9px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: THEME.accent,
                          fontSize: "15px",
                          pointerEvents: "none",
                        }}
                      />
                    </div>
                  </div>

                  {/* Allow Decimal */}
                  <div>
                    <label
                      style={{
                        ...labelStyle,
                        fontSize: "12px",
                        marginBottom: "4px",
                      }}
                    >
                      Allow Decimal
                    </label>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        height: "36px",
                        borderRadius: "7px",
                        border: "1px solid #E2E8F0",
                        overflow: "hidden",
                        background: "#FFFFFF",
                      }}
                    >
                      {["Yes", "No"].map((opt) => {
                        const isSelected = newProductData.allowDecimal === opt;
                        return (
                          <label
                            key={opt}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12.5px",
                              fontWeight: isSelected ? 700 : 500,
                              cursor: "pointer",
                              background: isSelected ? THEME.light : "#FFFFFF",
                              color: isSelected ? THEME.primary : THEME.muted,
                              borderRight:
                                opt === "Yes" ? "1px solid #E2E8F0" : "none",
                              transition: "all 0.15s ease",
                            }}
                          >
                            <input
                              type="radio"
                              name="allowDecimal"
                              value={opt}
                              checked={newProductData.allowDecimal === opt}
                              onChange={handleNewProductChange}
                              style={{ display: "none" }}
                            />
                            {opt}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Action Buttons in Form Footer */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: "10px",
                    marginTop: "6px",
                    paddingTop: "12px",
                    borderTop: "1px solid #F1F5F9",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowAddProductView(false)}
                    disabled={savingNewProduct}
                    style={{
                      padding: "7px 15px",
                      borderRadius: "7px",
                      border: "1px solid #D1D5DB",
                      background: "#FFFFFF",
                      color: THEME.muted,
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={handleResetNewProduct}
                    disabled={savingNewProduct}
                    style={{
                      padding: "7px 13px",
                      borderRadius: "7px",
                      border: "1px solid #D1D5DB",
                      background: "#FFFFFF",
                      color: THEME.muted,
                      fontSize: "13px",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      cursor: "pointer",
                    }}
                  >
                    <RestartAltOutlined style={{ fontSize: "16px" }} />
                    Reset
                  </button>

                  <button
                    type="submit"
                    disabled={savingNewProduct}
                    style={{
                      padding: "7px 18px",
                      borderRadius: "7px",
                      border: "none",
                      background: savingNewProduct
                        ? THEME.muted
                        : THEME.primary,
                      color: "#FFFFFF",
                      fontSize: "13px",
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: savingNewProduct ? "not-allowed" : "pointer",
                      boxShadow: "0 2px 6px rgba(109,40,217,0.25)",
                    }}
                  >
                    <SaveIcon style={{ fontSize: "16px" }} />
                    {savingNewProduct
                      ? "Saving & Adding..."
                      : "Save & Select Product"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* =========================================================
          SNACKBAR NOTIFICATION
      ========================================================== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{
            width: "100%",
            fontWeight: 600,
            borderRadius: "8px",
            boxShadow: "0 4px 16px rgba(15, 23, 42, 0.1)",
            backgroundColor:
              snackbar.severity === "success" ? THEME.primaryDark : undefined,
            color: snackbar.severity === "success" ? "#FFFFFF" : undefined,
            "& .MuiAlert-icon": {
              color: snackbar.severity === "success" ? "#DDD6FE" : undefined,
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* =========================================================
          RESPONSIVE STYLES
      ========================================================== */}
      <style>
        {`
          /* Line Items Grid Table & Focus Highlight */
          .receipt-grid-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 13.5px;
          }
          .receipt-grid-table td {
            border: 1px solid #CBD5E1;
            padding: 0;
            height: 38px;
            box-sizing: border-box;
          }
          .receipt-grid-cell-input {
            width: 100%;
            height: 100%;
            min-height: 38px;
            border: none !important;
            outline: none !important;
            border-radius: 0 !important;
            background: transparent;
            box-sizing: border-box;
            font-family: inherit;
            font-size: 13.5px;
            color: #1E293B;
            transition: background-color 0.12s ease;
          }
          .receipt-grid-cell-input:focus {
            background-color: #FEF08A !important;
            box-shadow: inset 0 0 0 2px #EAB308 !important;
          }
          .receipt-grid-cell-input::-webkit-outer-spin-button,
          .receipt-grid-cell-input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          .receipt-grid-cell-input[type=number] {
            -moz-appearance: textfield;
          }
          .receipt-grid-roller-btn {
            transition: background-color 0.12s ease;
          }
          .receipt-grid-roller-btn:focus {
            background-color: #FEF08A !important;
            box-shadow: inset 0 0 0 2px #EAB308 !important;
          }

          /* Supplier Modal & Add Supplier Form Input Focus Highlight */
          .supplier-modal-form input:focus,
          .supplier-modal-form select:focus,
          .supplier-modal-form textarea:focus,
          .supplier-search-input:focus {
            background-color: #FEF08A !important;
            border-color: #EAB308 !important;
            box-shadow: 0 0 0 2px rgba(234, 179, 8, 0.25) !important;
            outline: none !important;
          }

          /* Full Row Focus Highlight */
          .receipt-grid-table tr:focus {
            outline: none;
          }
          .receipt-grid-table tr.receipt-row-focused {
            background-color: #EDE9FE !important;
          }
          .receipt-grid-table tr.receipt-row-focused td {
            background-color: #EDE9FE !important;
            border-top: 2px solid #6D28D9 !important;
            border-bottom: 2px solid #6D28D9 !important;
          }
          .receipt-grid-table tr.receipt-row-focused td:first-child {
            border-left: 5px solid #6D28D9 !important;
          }
          .receipt-grid-table tr.receipt-row-focused td:last-child {
            border-right: 2px solid #6D28D9 !important;
          }

          .receipt-table-scroll {
            scroll-padding-top: 42px;
          }
          .receipt-grid-table tr,
          .receipt-grid-table td,
          .receipt-grid-cell-input,
          .receipt-grid-roller-btn {
            scroll-margin-top: 42px;
          }

          .receipt-table-scroll::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          .receipt-table-scroll::-webkit-scrollbar-track {
            background: #F8FAFC;
          }
          .receipt-table-scroll::-webkit-scrollbar-thumb {
            background: #CBD5E1;
            border-radius: 4px;
          }
          .receipt-table-scroll::-webkit-scrollbar-thumb:hover {
            background: #94A3B8;
          }
          @media (max-width: 1100px) {
            .receipt-summary-row-1 {
              grid-template-columns: repeat(3, 1fr) !important;
            }
            .receipt-summary-row-2 {
              grid-template-columns: 1fr 1fr !important;
            }
          }
          @media (max-width: 991px) {
            .receipt-header-grid {
              grid-template-columns: 1fr 1fr !important;
            }
            .receipt-summary-row-1 {
              grid-template-columns: repeat(2, 1fr) !important;
            }
            .receipt-summary-row-2 {
              grid-template-columns: 1fr !important;
            }
            .receipt-summary-row-2 > button {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          @media (max-width: 640px) {
            .receipt-header-grid {
              grid-template-columns: 1fr !important;
            }
            .receipt-summary-row-1 {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Receipt;
